/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * =================================================================================================
 * CUSTOM ENHANCEMENTS
 * =================================================================================================
 */

/**
 * Product Block Color Swatch Enhancement
 * - Reverts to original image when mouse leaves color swatches
 * - Only affects color swatches (not size swatches which don't have data-media attribute)
 */
(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwatchRevert);
  } else {
    initSwatchRevert();
  }

  function initSwatchRevert() {
    // Use MutationObserver to handle dynamically loaded product blocks
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'PRODUCT-BLOCK') {
              setupSwatchRevert(node);
            }
            // Also check descendants
            node.querySelectorAll && node.querySelectorAll('product-block').forEach(setupSwatchRevert);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Setup existing product blocks
    document.querySelectorAll('product-block').forEach(setupSwatchRevert);
  }

  function setupSwatchRevert(productBlock) {
    // Skip if already set up
    if (productBlock.hasAttribute('data-swatch-revert-enabled')) return;
    productBlock.setAttribute('data-swatch-revert-enabled', 'true');

    // Wait for the component to initialize (it uses lazy loading)
    const checkInit = setInterval(() => {
      if (productBlock.images && productBlock.images.length > 1) {
        clearInterval(checkInit);
        attachSwatchRevertHandlers(productBlock);
      }
    }, 100);

    // Stop checking after 5 seconds
    setTimeout(() => clearInterval(checkInit), 5000);
  }

  function attachSwatchRevertHandlers(productBlock) {
    // Store the initial active image index
    let initialImageIndex = 0;
    for (let i = 0; i < productBlock.images.length; i++) {
      if (productBlock.images[i].classList.contains('product-block__image--active')) {
        initialImageIndex = i;
        break;
      }
    }
    productBlock._initialImageIndex = initialImageIndex;

    // Find the color swatch container (has --swatch class, which means it's NOT a text swatch)
    const colorSwatchContainers = productBlock.querySelectorAll('.product-block-options--swatch');

    colorSwatchContainers.forEach((container) => {
      // Track if we're hovering over any swatch in this container
      let isHoveringContainer = false;

      // When mouse enters the swatch container area
      container.addEventListener('mouseenter', () => {
        isHoveringContainer = true;
      });

      // When mouse leaves the entire swatch container
      container.addEventListener('mouseleave', () => {
        isHoveringContainer = false;
        // Revert to initial image
        if (typeof productBlock.setActiveImage === 'function') {
          productBlock.setActiveImage(productBlock._initialImageIndex);
          
          // Also revert the product links back to original URLs
          const baseUrl = productBlock.querySelector('.product-link')?.href?.split('?')[0];
          if (baseUrl) {
            productBlock.querySelectorAll('.product-link, .quickbuy-toggle').forEach((el) => {
              if (el.href) {
                el.href = baseUrl;
                el.removeAttribute('rel');
              }
            });
          }
        }
      });
    });

    // Also update the initial image index when model toggle is used
    const modelToggleButtons = document.querySelectorAll('.collection-model-toggle-button');
    modelToggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        // Delay to allow the toggle to complete
        setTimeout(() => {
          for (let i = 0; i < productBlock.images.length; i++) {
            if (productBlock.images[i].classList.contains('product-block__image--active')) {
              productBlock._initialImageIndex = i;
              break;
            }
          }
        }, 100);
      });
    });
  }
})();

/* ==========================================================================
  MOBILE QUICK-ADD: BOTTOM SHEET MODAL
  On mobile, quick buy opens a sticky bottom-sheet modal with product info,
  variant options, and Add to cart. Close via X button or clicking outside.
  ========================================================================== */
(function() {
  'use strict';

  if (!window.theme || theme.settings.quickbuyStyle === 'off') return;

  const MOBILE_BREAKPOINT = 768;

  function getMoneyFormat(useNoDecimals) {
    const format = theme.settings.moneyWithCurrencyFormat || '€{{amount}}';
    if (!useNoDecimals) return format;
    if (/\{\{\s*amount_no_decimals/.test(format)) return format;
    if (format.includes('amount_with_comma_separator')) {
      return format.replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator');
    }
    if (format.includes('amount_with_apostrophe_separator')) {
      return format.replace('amount_with_apostrophe_separator', 'amount_no_decimals_with_apostrophe_separator');
    }
    return format.replace('amount', 'amount_no_decimals');
  }

  function formatMoney(cents, useNoDecimals = true) {
    let value = cents;
    if (typeof value === 'string') value = parseInt(value.replace('.', ''), 10);
    if (Number.isNaN(value)) return '';

    const moneyFormat = getMoneyFormat(useNoDecimals);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatMatch = moneyFormat.match(placeholderRegex);
    const placeholder = formatMatch ? formatMatch[1] : 'amount';

    function defaultTo(v, d) {
      return v == null || v !== v ? d : v;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const p = defaultTo(precision, 2);
      const t = defaultTo(thousands, '.');
      const dec = defaultTo(decimal, ',');
      if (Number.isNaN(number) || number == null) return '0';
      const n = (number / 100.0).toFixed(p);
      const parts = n.split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${t}`);
      return intPart + (parts[1] ? dec + parts[1] : '');
    }

    let formatted;
    switch (placeholder) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 0, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 0, '.', ',');
    }
    return moneyFormat.replace(placeholderRegex, formatted);
  }

  function formatMoneyHideEur(cents) {
    return formatMoney(cents, true).replace(/\s*EUR\s*$/i, '');
  }

  function normalizeProductEdition(edition) {
    if (edition == null || edition === '') return '';
    return String(edition).replace(/[\[\]"]/g, '').split(',')[0].trim().toLowerCase();
  }

  function isOutletEdition(edition) {
    return normalizeProductEdition(edition) === 'outlet';
  }

  function isVariantOnSale(variant) {
    if (!variant) return false;
    const compareAt = variant.compare_at_price;
    return compareAt && compareAt > variant.price;
  }

  function shouldShowFryklandInModal(variant, edition) {
    return isVariantOnSale(variant) && !isOutletEdition(edition);
  }

  function getModalFryklandEl(container) {
    return container.querySelector('.mobile-quick-add-modal__frykland');
  }

  async function fetchMainProductSectionHtml(productPageUrl) {
    const cacheKey = 'aesthetMainProductSectionId';
    let sectionId = null;
    try {
      sectionId = sessionStorage.getItem(cacheKey);
    } catch (e) {
      sectionId = null;
    }

    if (!sectionId) {
      const pageUrl = new URL(productPageUrl, window.location.origin);
      const pageRes = await fetch(pageUrl.toString(), { credentials: 'same-origin' });
      if (!pageRes.ok) return '';
      const pageHtml = await pageRes.text();
      const sectionMatch = pageHtml.match(/id="shopify-section-(template--\d+__main)"/);
      if (!sectionMatch) return '';
      sectionId = sectionMatch[1];
      try {
        sessionStorage.setItem(cacheKey, sectionId);
      } catch (e) {
        /* ignore */
      }
    }

    const sectionUrl = new URL(productPageUrl, window.location.origin);
    sectionUrl.searchParams.set('section_id', sectionId);
    const sectionRes = await fetch(sectionUrl.toString(), { credentials: 'same-origin' });
    if (!sectionRes.ok) return '';
    return sectionRes.text();
  }

  function parseFryklandDataFromHtml(html) {
    if (!html || !html.includes('fryk-variants-json')) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const jsonEl = doc.querySelector('script[id^="fryk-variants-json"]');
    if (!jsonEl) return null;

    let variants = [];
    try {
      variants = JSON.parse(jsonEl.textContent || '[]');
    } catch (e) {
      return null;
    }
    if (!variants.length) return null;

    let label = 'Lowest price in the last 30 days:';
    const wrapper = doc.querySelector('[id^="frykland-lowestprice-wrapper"]');
    if (wrapper) {
      const clone = wrapper.cloneNode(true);
      clone.querySelectorAll('span, script, style').forEach((el) => el.remove());
      const text = clone.textContent.replace(/\s+/g, ' ').trim();
      if (text) label = text;
    }

    return {
      label,
      byId: new Map(variants.map((v) => [String(v.id), v]))
    };
  }

  async function fetchFryklandData(productPageUrl) {
    const html = await fetchMainProductSectionHtml(productPageUrl);
    return parseFryklandDataFromHtml(html);
  }

  function getFryklandRowForVariant(fryklandData, variant) {
    if (!fryklandData || !variant?.id) return null;
    const entry = fryklandData.byId.get(String(variant.id));
    if (!entry || entry.on_sale !== true) return null;
    const displayTxt = entry.display_txt || entry.lowest_txt || '';
    if (!displayTxt) return null;
    return {
      label: fryklandData.label,
      displayTxt,
      percentTxt: entry.percent_txt || ''
    };
  }

  function buildFryklandRowHtml(row) {
    if (!row) return '';
    const pct = row.percentTxt
      ? ` <span class="mobile-quick-add-modal__frykland-percent">${escapeHtmlText(row.percentTxt)}</span>`
      : '';
    return `<div class="mobile-quick-add-modal__frykland">${escapeHtmlText(row.label)} <span class="mobile-quick-add-modal__frykland-price">${escapeHtmlText(row.displayTxt)}</span>${pct}</div>`;
  }

  function setModalFryklandRow(contentEl, row) {
    const existing = getModalFryklandEl(contentEl);
    if (!row) {
      existing?.remove();
      return;
    }
    const html = buildFryklandRowHtml(row);
    if (existing) {
      existing.outerHTML = html;
      return;
    }
    const priceWrap = contentEl.querySelector('.mobile-quick-add-modal__price-wrap');
    if (priceWrap) {
      priceWrap.insertAdjacentHTML('afterend', html);
    } else {
      contentEl.querySelector('.mobile-quick-add-modal__header')?.insertAdjacentHTML('beforeend', html);
    }
  }

  function loadFryklandIntoModal(modal, contentEl, productPageUrl, variant, edition) {
    if (!shouldShowFryklandInModal(variant, edition)) return;
    if (contentEl.dataset.fryklandLoading === '1') return;

    contentEl.dataset.fryklandLoading = '1';
    delete contentEl._fryklandData;

    fetchFryklandData(productPageUrl).then((fryklandData) => {
      contentEl.dataset.fryklandLoading = '0';
      if (!modal.classList.contains('is-open')) return;

      if (!fryklandData) {
        getModalFryklandEl(contentEl)?.remove();
        return;
      }

      contentEl._fryklandData = fryklandData;
      const row = getFryklandRowForVariant(fryklandData, variant);
      if (row && shouldShowFryklandInModal(variant, edition)) {
        setModalFryklandRow(contentEl, row);
      }
    });
  }

  function syncModalFryklandVisibility(contentEl, variant, edition) {
    if (!shouldShowFryklandInModal(variant, edition)) {
      getModalFryklandEl(contentEl)?.remove();
      return;
    }
    if (contentEl._fryklandData) {
      setModalFryklandRow(contentEl, getFryklandRowForVariant(contentEl._fryklandData, variant));
    }
  }

  function isMemberPricingExcluded(productData) {
    if (!productData) return true;
    const excluded = theme.settings.memberExcludedTags || ['gift_card', 'gift_box', 'gift_bag', 'no discount gold'];
    const tags = String(productData.tags || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
    return excluded.some((tag) => tags.includes(String(tag).trim().toLowerCase()));
  }

  function computeMemberPricing(variant, productData) {
    const memberPct = theme.settings.memberTierPercent || 0;
    if (!memberPct || !variant || isMemberPricingExcluded(productData)) {
      return { showMember: false };
    }

    const price = variant.price;
    const compareAt = variant.compare_at_price || 0;
    let salePct = 0;
    if (compareAt > price && compareAt > 0) {
      salePct = Math.round(((compareAt - price) * 100) / compareAt);
    }
    if (memberPct <= salePct) {
      return { showMember: false };
    }

    const multiplier = 100 - memberPct;
    let wasPrice = price;
    let displayPrice;
    if (compareAt > price && compareAt > 0) {
      wasPrice = compareAt;
      displayPrice = Math.floor((compareAt * multiplier) / 100);
    } else {
      displayPrice = Math.floor((price * multiplier) / 100);
    }
    displayPrice = Math.round((displayPrice + 50) / 100) * 100;

    return {
      showMember: true,
      memberPct,
      displayPrice,
      wasPrice,
    };
  }

  function buildMemberPriceHtml(memberPricing) {
    const label = String(theme.strings?.memberPriceLabel || 'Member Price').toUpperCase();
    const wasHtml = `<span class="theme-money struck-out-price js-value" data-cc-cents="${memberPricing.wasPrice}">${formatMoneyHideEur(memberPricing.wasPrice)}</span>`;
    const finalHtml = `<span class="js-value theme-money" data-cc-cents="${memberPricing.displayPrice}">${formatMoneyHideEur(memberPricing.displayPrice)}</span>`;

    return `
      <div class="mobile-quick-add-modal__price-wrap price price--member-pricing">
        <div class="price__default">
          <div class="price__stack price__stack--member">
            <span class="small-text price__compare-row price__compare-row--was price__stack__was">
              ${wasHtml}
            </span>
            <div class="price__row price__row--member">
              <span class="price__current price__stack__final">${finalHtml}</span>
              <span class="price__member-label">${escapeHtmlText(label)}</span>
            </div>
          </div>
        </div>
      </div>`;
  }

  function buildPriceHtml(variant, edition, productData) {
    const memberPricing = computeMemberPricing(variant, productData);
    if (memberPricing.showMember) {
      return buildMemberPriceHtml(memberPricing);
    }

    const price = variant.price;
    const compareAt = variant.compare_at_price;
    const isOnSale = compareAt && compareAt > price;
    const sellingPrice = `<span class="js-value theme-money" data-cc-cents="${price}">${formatMoneyHideEur(price)}</span>`;

    if (!isOnSale) {
      return `<div class="mobile-quick-add-modal__price-wrap"><div class="mobile-quick-add-modal__price price"><span class="price__current">${sellingPrice}</span></div></div>`;
    }

    const discountPercent = Math.round(((compareAt - price) * 100) / compareAt);
    const useCatalogue = theme.settings.cataloguePriceForOutletShow && isOutletEdition(edition);
    const cataloguePrefix = escapeHtmlText(theme.strings?.cataloguePrice || 'Catalogue Price:');

    if (useCatalogue) {
      return `
        <div class="mobile-quick-add-modal__price-wrap price price--on-sale price--stacked-sale">
          <div class="price__default">
            <div class="price__row price__row--current">
              <span class="price__current">${sellingPrice}</span>
            </div>
            <div class="price__row price__row--catalogue">
              <span class="price-label price-label--catalogue-price">
                <span class="price-label__prefix">${cataloguePrefix}</span>
                <span class="price-label__value theme-money">${formatMoneyHideEur(compareAt)}</span>
              </span>
            </div>
          </div>
        </div>`;
    }

    return `
      <div class="mobile-quick-add-modal__price-wrap price price--on-sale price--stacked-sale">
        <div class="price__default">
          <div class="price__row price__row--current">
            <span class="price__current">${sellingPrice}</span>
          </div>
          <div class="price__row price__row--was">
            <span class="price__was theme-money">${formatMoneyHideEur(compareAt)}</span>
            <span class="price__discount-percent">-${discountPercent}%</span>
          </div>
        </div>
      </div>`;
  }

  function syncModalApproxPrices(container) {
    if (!window.aesthetCurrency?.refreshEl) return;
    requestAnimationFrame(() => {
      container.querySelectorAll('.mobile-quick-add-modal__price-wrap .price__stack__final > .js-value[data-cc-cents], .mobile-quick-add-modal__price-wrap .price__current > .js-value[data-cc-cents]').forEach((el) => {
        window.aesthetCurrency.refreshEl(el);
      });
    });
  }

  const PLP_TEXT_SIZE_ALIASES = { size: 1, sizes: 1, 'μέγεθος': 1, 'μεγεθος': 1, 'μεγέθη': 1, 'μέγεθη': 1 };

  function isPlpTextThumbOptionName(nameLower, textNames) {
    if (textNames.includes(nameLower)) return true;
    const configuredHasTextOptions = textNames.length > 0;
    if (!configuredHasTextOptions) return false;
    if (PLP_TEXT_SIZE_ALIASES[nameLower]) return true;
    if (nameLower.includes('size')) return true;
    if (nameLower.includes('εθ') || nameLower.includes('έθ')) return true;
    return nameLower.includes('taille')
      || nameLower.includes('grosse')
      || nameLower.includes('groesse')
      || nameLower.includes('größe');
  }

  function productHasColorOptions(productData) {
    const swatchNames = String(theme.settings.swatchOptionNames || 'Color')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const textNames = String(theme.settings.prodThumbOptionsNames || 'Size')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const allOptionNames = (productData.options || []).map((o) => (typeof o === 'string' ? o : o.name));

    return allOptionNames.some((name) => {
      const nameLower = String(name).toLowerCase();
      if (isPlpTextThumbOptionName(nameLower, textNames)) return false;
      if (!swatchNames.includes(nameLower)) return false;
      const optIndex = allOptionNames.indexOf(name);
      const values = [...new Set(productData.variants.map((v) => v['option' + (optIndex + 1)]).filter(Boolean))];
      return values.length > 1;
    });
  }

  function buildMoreColorsHtml(productBlock, productData, productPageUrl) {
    const labelLink = productBlock?.querySelector('.product-block-options--label .product-block-options__label-link');
    const show = labelLink || productHasColorOptions(productData);
    if (!show) return '';
    const label = labelLink?.textContent?.trim() || theme.strings?.moreColors || 'More Colors';
    return `<a href="${productPageUrl}" class="mobile-quick-add-modal__more-colors product-block-options__label-link">${escapeHtmlText(label)}</a>`;
  }

  function isProductOutOfStock(productData) {
    return !productData.available || !productData.variants.some((v) => v.available);
  }

  function isPreorderAvailabilityProduct(productBlock) {
    return productBlock?.dataset?.preorderAvailability === 'true';
  }

  function buildOosActionsHtml(productBlock, productData) {
    if (!isProductOutOfStock(productData)) return '';
    if (isPreorderAvailabilityProduct(productBlock)) return '';

    const notifyRef = productBlock?.querySelector('[data-plp-notify-when-available]');
    const viewSimilarRef = productBlock?.querySelector('[data-plp-view-similar]');
    const notifyLabel = notifyRef?.textContent?.trim()
      || theme.strings?.notifyWhenAvailable
      || 'Notify me when available';
    const viewSimilarLabel = viewSimilarRef?.getAttribute('aria-label')?.trim()
      || theme.strings?.viewSimilar
      || 'View similar';
    const bisVariantId = productData.variants?.[0]?.id || '';

    return `
      <div class="mobile-quick-add-modal__hover-actions product-block__hover-actions">
        <button type="button" class="btn btn--secondary product-block__hover-action-btn product-block__hover-action-btn--notify klaviyo-bis-trigger" klaviyo-bis-trigger data-plp-notify-when-available data-product-id="${productData.id}" data-variant-id="${bisVariantId}" data-product-handle="${escapeHtmlAttr(productData.handle)}" aria-label="${escapeHtmlAttr(notifyLabel)}">${escapeHtmlText(notifyLabel)}</button>
        <div class="align-center">
          <a href="/products/${escapeHtmlAttr(productData.handle)}#pdp-view-similar" class="small-feature-link small-feature-link--chevron product-block__hover-action-btn product-block__hover-action-btn--view-similar" data-plp-view-similar data-product-id="${productData.id}" data-product-handle="${escapeHtmlAttr(productData.handle)}" aria-label="${escapeHtmlAttr(viewSimilarLabel)}">
            ${escapeHtmlText(viewSimilarLabel)}
            <span class="small-feature-link__chevron" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-right" aria-hidden="true" focusable="false" role="presentation"><path d="m9 18 6-6-6-6"/></svg></span>
          </a>
        </div>
      </div>`;
  }

  /** Match snippets/option-value-display.liquid + product tile PLP labels */
  function formatOptionValueForDisplay(text) {
    const t = String(text).trim();
    if (t.includes(' ')) return t.toUpperCase();
    return t;
  }

  function escapeHtmlAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeHtmlText(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Match snippets/product-storefront-title.liquid — strip admin-prepended vendor prefix */
  function getStorefrontTitle(title, vendor) {
    const vendorTrimmed = String(vendor || '').trim();
    if (!vendorTrimmed) return title;
    const parts = String(title).split(' | ');
    if (parts.length > 1 && parts[0] === vendorTrimmed) {
      return parts.slice(1).join(' | ');
    }
    return title;
  }

  /** Match snippets/product-option-is-placeholder.liquid */
  function isPlaceholderOptionValue(value) {
    const v = String(value || '').trim().toLowerCase();
    return v === 'default title' || v === 'default tile';
  }

  /** Shopify default option for products with no real variants (e.g. one-size bags). */
  function isPlaceholderProductOption(optionName, values) {
    const name = String(optionName || '').trim().toLowerCase();
    if (name === 'title') return true;
    const displayValues = values.filter((v) => !isPlaceholderOptionValue(v));
    if (displayValues.length === 0) return true;
    return false;
  }

  function getOptionDisplayValues(productData, allOptionNames, optName) {
    const optIndex = allOptionNames.indexOf(optName);
    const values = [...new Set(productData.variants.map((v) => v['option' + (optIndex + 1)]).filter(Boolean))];
    return values.filter((v) => !isPlaceholderOptionValue(v));
  }

  function getOrCreateModal() {
    let modal = document.getElementById('mobile-quick-add-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'mobile-quick-add-modal';
      modal.className = 'mobile-quick-add-modal';
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.innerHTML = `
        <div class="mobile-quick-add-modal__backdrop" aria-hidden="true"></div>
        <div class="mobile-quick-add-modal__sheet">
          <button type="button" class="mobile-quick-add-modal__close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <div class="mobile-quick-add-modal__content"></div>
        </div>
      `;
      document.body.appendChild(modal);

      const closeModal = () => {
        closeMobileQuickAddModalInstance(modal);
      };

      modal.querySelector('.mobile-quick-add-modal__backdrop').addEventListener('click', closeModal);
      modal.querySelector('.mobile-quick-add-modal__close').addEventListener('click', closeModal);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          closeModal();
        }
      });
    }
    return modal;
  }

  function refreshCartPageAfterAdd() {
    if (!document.querySelector('.template-cart')) return false;
    const cartForm = document.querySelector('cart-form');
    if (cartForm && cartForm.enableAjaxUpdate && typeof cartForm.refresh === 'function') {
      cartForm.refresh();
      return true;
    }
    return false;
  }

  function restoreMobileQuickAddModalToBody(modal) {
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  /** Desktop cart drawer: mount quick-add inside the drawer panel (mobile-style “phone screen”). */
  function mountCartDrawerQuickAddModal(modal) {
    if (!modal) return;

    const useDrawerMount = modal.classList.contains('mobile-quick-add-modal--from-cart-drawer')
      && window.innerWidth >= MOBILE_BREAKPOINT;

    if (useDrawerMount) {
      const drawer = document.querySelector('cart-drawer.js-cart-drawer[open]');
      if (drawer && modal.parentElement !== drawer) {
        drawer.appendChild(modal);
      }
      return;
    }

    restoreMobileQuickAddModalToBody(modal);
  }

  function closeMobileQuickAddModalInstance(modal) {
    if (!modal) return;
    modal.classList.remove('is-open', 'mobile-quick-add-modal--from-cart-drawer');
    modal.setAttribute('aria-hidden', 'true');
    restoreMobileQuickAddModalToBody(modal);
    document.body.style.overflow = '';
  }

  function initCartDrawerUpsellQuickAdd(root) {
    if (!window.theme || theme.settings.quickbuyStyle === 'off') return;

    const scope = root && typeof root.querySelector === 'function' ? root : document;
    if (!scope.querySelectorAll) return;

    scope.querySelectorAll('.cart-drawer .cart-item-upsells product-block.product-block--image-only').forEach((productBlock) => {
      const imageCont = productBlock.querySelector('.image-cont');
      if (!imageCont) return;

      const productUrl = productBlock.querySelector('.product-link')?.getAttribute('href');
      if (!productUrl) return;

      let btn = imageCont.querySelector('.cart-drawer-upsell-quickadd');
      if (!btn) {
        btn = document.createElement('a');
        btn.className = 'btn btn--secondary quickbuy-toggle cart-drawer-upsell-quickadd';
        imageCont.appendChild(btn);
      }

      btn.href = productUrl;
      btn.setAttribute('aria-label', theme.strings?.quickBuy || 'Quick buy');
    });
  }

  ['cart-cross-sell:recommendations-loaded', 'on:cart:after-merge', 'on:cart:add'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      queueMicrotask(() => initCartDrawerUpsellQuickAdd(document));
    });
  });

  document.addEventListener('on:cart-drawer:before-open', (evt) => {
    initCartDrawerUpsellQuickAdd(evt.target);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCartDrawerUpsellQuickAdd(document));
  } else {
    initCartDrawerUpsellQuickAdd(document);
  }

  document.addEventListener('click', (evt) => {
    const isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;

    let quickbuyBtn = evt.target.closest('.quickbuy-toggle');
    let productBlock = quickbuyBtn?.closest('product-block');

    // Cart drawer “Complete the look”: image tiles keep no_quick_buy but open the quick-add sheet on tap / +
    if (!quickbuyBtn) {
      const drawerUpsellLink = evt.target.closest('.cart-drawer .cart-item-upsells .product-link');
      if (drawerUpsellLink) {
        quickbuyBtn = drawerUpsellLink;
        productBlock = drawerUpsellLink.closest('product-block');
      }
    }

    if (!quickbuyBtn || !productBlock || !productBlock.dataset.productHandle) return;

    const isCartDrawerUpsell = Boolean(productBlock.closest('.cart-drawer .cart-item-upsells'));
    if (isDesktop && !isCartDrawerUpsell) return;

    evt.preventDefault();
    evt.stopPropagation();

    openMobileQuickAddModal(
      productBlock.dataset.productHandle,
      productBlock.querySelector('.product-link')?.href,
      productBlock
    );
  }, true);

  async function openMobileQuickAddModal(productHandle, productUrl, productBlock) {
    let productData = null;
    try {
      const res = await fetch(`/products/${productHandle}.js`);
      productData = await res.json();
    } catch (e) {
      window.location.href = productUrl || `/products/${productHandle}`;
      return;
    }

    const modal = getOrCreateModal();
    const isCartDrawerUpsell = Boolean(productBlock?.closest('.cart-drawer .cart-item-upsells'));
    modal.classList.toggle(
      'mobile-quick-add-modal--from-cart-drawer',
      isCartDrawerUpsell && window.innerWidth >= MOBILE_BREAKPOINT
    );
    mountCartDrawerQuickAddModal(modal);
    const contentEl = modal.querySelector('.mobile-quick-add-modal__content');

    const allOptionNames = (productData.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const colorOptionNames = ['color', 'colour', 'χρώμα', 'colore', 'couleur', 'farbe', 'kleur', 'cor', '색상', '色'];
    const optionNames = allOptionNames.filter((name) => {
      if (colorOptionNames.includes(String(name).toLowerCase())) return false;
      const optIndex = allOptionNames.indexOf(name);
      const values = [...new Set(productData.variants.map((v) => v['option' + (optIndex + 1)]).filter(Boolean))];
      return !isPlaceholderProductOption(name, values);
    });
    const selectedOptions = {};
    let selectedVariantId = productData.variants.length === 1 ? productData.variants[0].id : null;

    function getVariantId() {
      if (productData.variants.length === 1) return productData.variants[0].id;
      if (optionNames.length === 0) return null;
      const values = optionNames.map((name) => selectedOptions[name] || null);
      if (values.some((v) => v == null)) return null;
      const matching = productData.variants.filter((variant) => {
        return optionNames.every((name, i) => {
          const optIndex = allOptionNames.indexOf(name);
          return String(variant['option' + (optIndex + 1)] || '') === String(values[i]);
        });
      });
      const v = matching.find((x) => x.available) || matching[0];
      return v ? v.id : null;
    }

    optionNames.forEach((optName) => {
      const displayValues = getOptionDisplayValues(productData, allOptionNames, optName);
      if (displayValues.length === 1) {
        selectedOptions[optName] = displayValues[0];
      }
    });

    const selectedVariant = selectedVariantId ? productData.variants.find((v) => v.id === selectedVariantId) : productData.variants[0];
    const priceVariant = selectedVariant || productData.variants[0];
    const productEdition = productBlock?.dataset?.productEdition || '';

    let optionsHtml = '';
    optionNames.forEach((optName) => {
      const optIndex = allOptionNames.indexOf(optName);
      const displayValues = getOptionDisplayValues(productData, allOptionNames, optName);
      if (displayValues.length === 0) return;
      optionsHtml += `<div class="mobile-quick-add-modal__option" data-option-name="${optName.replace(/"/g, '&quot;')}" data-option-index="${optIndex}">`;
      displayValues.forEach((val) => {
        const matchingVariants = productData.variants.filter((v) => String(v['option' + (optIndex + 1)]) === String(val));
        const available = matchingVariants.some((v) => v.available);
        const disabledClass = !available ? ' mobile-quick-add-modal__option-item--unavailable' : '';
        const selectedClass = displayValues.length === 1 || selectedOptions[optName] === val ? ' selected' : '';
        const label = escapeHtmlText(formatOptionValueForDisplay(val));
        const vn = String(val).trim();
        const isWideLabel = vn.includes(' ') || vn.length > 4;
        const wideClass = isWideLabel ? ' mobile-quick-add-modal__option-item--wide' : '';
        const labelHtml = isWideLabel
          ? `<span class="product-block-options__item__text">${label}</span>`
          : label;
        optionsHtml += `<button type="button" class="mobile-quick-add-modal__option-item${wideClass}${disabledClass}${selectedClass}" data-option-value="${escapeHtmlAttr(String(val))}" ${!available ? 'disabled' : ''}>${labelHtml}</button>`;
      });
      optionsHtml += '</div>';
    });

    const productPageUrl = productUrl || `/products/${productHandle}`;
    const storefrontTitle = getStorefrontTitle(productData.title, productData.vendor);
    const moreColorsHtml = buildMoreColorsHtml(productBlock, productData, productPageUrl);
    const isPreorder = isPreorderAvailabilityProduct(productBlock);
    const isOutOfStock = isProductOutOfStock(productData);
    const oosActionsHtml = isOutOfStock && !isPreorder ? buildOosActionsHtml(productBlock, productData) : '';
    const atcHtml = !isOutOfStock || isPreorder
      ? `<div class="mobile-quick-add-modal__atc-wrap">
        <button type="button" class="mobile-quick-add-modal__atc btn btn--brand btn--wide" data-variant-id="${selectedVariantId || ''}">ADD TO BAG</button>
        <div class="mobile-quick-add-modal__feedback" aria-live="polite"></div>
      </div>`
      : '';

    delete contentEl._fryklandData;
    contentEl.dataset.fryklandLoading = '0';

    contentEl.innerHTML = `
      <div class="mobile-quick-add-modal__header">
        ${productData.vendor ? `<div class="mobile-quick-add-modal__vendor">${escapeHtmlText(productData.vendor)}</div>` : ''}
        <h3 class="mobile-quick-add-modal__title"><a href="${productPageUrl}" class="mobile-quick-add-modal__title-link">${escapeHtmlText(storefrontTitle)}</a></h3>
        ${priceVariant ? buildPriceHtml(priceVariant, productEdition, productData) : ''}
      </div>
      ${optionsHtml ? `<div class="mobile-quick-add-modal__options-wrap"><div class="mobile-quick-add-modal__options">${optionsHtml}</div></div>` : ''}
      ${moreColorsHtml}
      ${oosActionsHtml}
      ${atcHtml}
      <a href="${productPageUrl}" class="mobile-quick-add-modal__details-link">Product Details</a>
    `;

    syncModalApproxPrices(contentEl);
    loadFryklandIntoModal(modal, contentEl, productPageUrl, priceVariant, productEdition);

    const atcBtn = contentEl.querySelector('.mobile-quick-add-modal__atc');
    const feedbackEl = contentEl.querySelector('.mobile-quick-add-modal__feedback');
    let feedbackTimer = null;

    function showSelectSizeFeedback() {
      if (!feedbackEl) return;
      feedbackEl.textContent = 'Select size';
      feedbackEl.classList.add('is-visible');
      if (feedbackTimer) clearTimeout(feedbackTimer);
      feedbackTimer = setTimeout(() => {
        feedbackEl.classList.remove('is-visible');
      }, 1400);
    }

    function updateAddToCartBtn() {
      if (!atcBtn) return;
      const missingRequiredSelection = optionNames.length > 0 && optionNames.some((name) => !selectedOptions[name]);
      const vid = selectedVariantId || getVariantId();
      const variant = vid && productData.variants.find((v) => v.id === parseInt(vid, 10));

      if (missingRequiredSelection) {
        atcBtn.disabled = false;
        atcBtn.classList.add('is-disabled');
        atcBtn.dataset.requiresSelection = 'true';
        atcBtn.dataset.variantId = '';
        return;
      }

      atcBtn.dataset.requiresSelection = 'false';
      const canAdd = variant && (variant.available || isPreorder);
      atcBtn.disabled = !canAdd;
      atcBtn.classList.toggle('is-disabled', atcBtn.disabled);
      atcBtn.dataset.variantId = vid || '';
    }

    contentEl.querySelectorAll('.mobile-quick-add-modal__option-item:not([disabled])').forEach((btn) => {
      btn.addEventListener('click', () => {
        const optName = btn.closest('.mobile-quick-add-modal__option')?.dataset?.optionName;
        const val = btn.dataset?.optionValue;
        if (!optName || !val) return;
        selectedOptions[optName] = val;
        selectedVariantId = null;
        btn.closest('.mobile-quick-add-modal__option')?.querySelectorAll('.mobile-quick-add-modal__option-item').forEach((b) => b.classList.remove('selected'));
        btn.classList.add('selected');
        const vid = getVariantId();
        if (vid) {
          selectedVariantId = vid;
          const variant = productData.variants.find((v) => v.id === parseInt(vid, 10));
          if (variant) {
            const priceWrap = contentEl.querySelector('.mobile-quick-add-modal__price-wrap');
            if (priceWrap) {
              const temp = document.createElement('div');
              temp.innerHTML = buildPriceHtml(variant, productEdition, productData);
              priceWrap.replaceWith(temp.firstElementChild);
              syncModalApproxPrices(contentEl);
            }
            syncModalFryklandVisibility(contentEl, variant, productEdition);
            if (shouldShowFryklandInModal(variant, productEdition) && !getModalFryklandEl(contentEl)) {
              loadFryklandIntoModal(modal, contentEl, productPageUrl, variant, productEdition);
            }
          }
        }
        updateAddToCartBtn();
      });
    });

    if (atcBtn) {
      if (productData.variants.length === 1) {
        selectedVariantId = productData.variants[0].id;
        atcBtn.disabled = !productData.variants[0].available && !isPreorder;
        atcBtn.classList.toggle('is-disabled', atcBtn.disabled);
        atcBtn.dataset.requiresSelection = 'false';
      } else {
        updateAddToCartBtn();
      }
    }

    atcBtn?.addEventListener('click', async () => {
      if (atcBtn.dataset.requiresSelection === 'true') {
        showSelectSizeFeedback();
        return;
      }

      const vid = atcBtn.dataset.variantId || selectedVariantId || getVariantId();
      if (!vid) return;
      atcBtn.disabled = true;
      atcBtn.classList.add('is-disabled');
      const originalText = atcBtn.textContent;
      atcBtn.textContent = '...';

      try {
        const formData = new FormData();
        formData.append('id', vid);
        formData.append('quantity', '1');
        const sections = ['page-header', 'cart-drawer'].map((s) => document.querySelector(s)).filter(Boolean);
        const sectionIds = sections.map((s) => s?.dataset?.sectionId).filter(Boolean);
        if (sectionIds.length) {
          formData.append('sections_url', window.location.pathname);
          formData.append('sections', sectionIds.join(','));
        }
        const res = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();
        if (data.status && data.message) {
          throw new Error(data.message);
        }
        if (data.sections && sections.length) {
          sections.forEach((el) => {
            if (el?.dataset?.sectionId && data.sections[el.dataset.sectionId]) {
              el.updateFromCartChange?.(data.sections[el.dataset.sectionId]);
            }
          });
        } else {
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }
        theme.setCartLastAddedProductId(data.product_id);
        document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: data.variant_id, productId: data.product_id } }));
        if (!refreshCartPageAfterAdd()) {
          if (theme.settings.afterAddToCart === 'drawer') {
            document.querySelector('.js-cart-drawer')?.open?.(atcBtn);
          } else if (theme.settings.afterAddToCart === 'notification') {
            const notif = document.getElementById('AddedNotification')?.content?.firstElementChild?.cloneNode(true);
            if (notif) {
              notif.dataset.productTitle = data.product_title || data.title || storefrontTitle || '';
              (document.querySelector('.pageheader--sticky') || document.body).appendChild(notif);
            }
          }
        }
        atcBtn.textContent = theme.strings?.addedToCartShort || 'Added';
        closeMobileQuickAddModalInstance(modal);
      } catch (err) {
        atcBtn.textContent = originalText;
        atcBtn.disabled = false;
        atcBtn.classList.remove('is-disabled');
        document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: err.message } }));
      }
    });

    mountCartDrawerQuickAddModal(modal);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    if (!modal.classList.contains('mobile-quick-add-modal--from-cart-drawer')) {
      document.body.style.overflow = 'hidden';
    }
    modal.querySelector('.mobile-quick-add-modal__close').focus();
  }

  window.addEventListener('on:debounced-resize', () => {
    const modal = document.getElementById('mobile-quick-add-modal');
    if (modal) mountCartDrawerQuickAddModal(modal);
  });

  /* PDPR desktop: inline size pickers + add to bag (mobile keeps + / bottom sheet) */
  const PDPR_QUICK_ADD_ROOT = '[data-pdpr-quick-add], [data-cart-cross-sell-quick-add]';
  const PDPR_SIZE_PICKER_SELECTOR = [
    '[data-pdpr-quick-add] .product-block--pdpr-add-to-bag .product-block__detail-hover .product-block-options__item--pdpr-picker:not(.product-block-options__item--unavailable):not([disabled])',
    '[data-cart-cross-sell-quick-add] .product-block--pdpr-add-to-bag .product-block__detail-hover .product-block-options__item--pdpr-picker:not(.product-block-options__item--unavailable):not([disabled])',
  ].join(', ');
  const PDPR_ADD_BTN_SELECTOR = [
    '[data-pdpr-quick-add] [data-pdpr-add-to-bag]',
    '[data-cart-cross-sell-quick-add] [data-pdpr-add-to-bag]',
  ].join(', ');
  const PDPR_PRODUCT_BLOCK_SELECTOR = [
    '[data-pdpr-quick-add] product-block.product-block--pdpr-add-to-bag',
    '[data-cart-cross-sell-quick-add] product-block.product-block--pdpr-add-to-bag',
  ].join(', ');

  function isPdprDesktop() {
    return window.innerWidth >= MOBILE_BREAKPOINT;
  }

  function isPdprRequiresSize(btn) {
    return btn?.getAttribute('data-pdpr-requires-size') === 'true';
  }

  function parseVariantIdFromHref(href) {
    if (!href) return null;
    try {
      const id = new URL(href, window.location.origin).searchParams.get('variant');
      return id ? parseInt(id, 10) : null;
    } catch {
      const match = href.match(/[?&]variant=(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }
  }

  function getVariantIdFromPicker(el) {
    const fromData = el.getAttribute('data-variant-id') || el.dataset?.variantId;
    if (fromData) return parseInt(fromData, 10) || null;
    return parseVariantIdFromHref(el.getAttribute('href'));
  }

  function getPdprSizePickers(productBlock) {
    const hover = productBlock.querySelector('.product-block__detail-hover');
    if (!hover) return [];
    return [...hover.querySelectorAll('.product-block-options__item--pdpr-picker:not(.product-block-options__item--unavailable):not([disabled])')];
  }

  function clearPdprSizeSelection(productBlock) {
    productBlock.querySelectorAll('.product-block-options__item--pdpr-selected').forEach((el) => {
      el.classList.remove('product-block-options__item--pdpr-selected');
      el.removeAttribute('aria-current');
    });
  }

  function setPdprAddButtonReady(btn, ready) {
    if (!btn) return;
    if (ready) {
      btn.setAttribute('data-pdpr-ready', 'true');
    } else {
      btn.removeAttribute('data-pdpr-ready');
    }
  }

  function updatePdprAddButton(productBlock, variantId) {
    const btn = productBlock.querySelector('[data-pdpr-add-to-bag]');
    if (!btn || !isPdprRequiresSize(btn) || btn.dataset.pdprFallbackModal === 'true') return;

    const labelAdd = btn.getAttribute('data-pdpr-label-add') || btn.dataset.pdprLabelAdd || 'ADD TO BAG';
    const labelSelect = btn.getAttribute('data-pdpr-label-select') || btn.dataset.pdprLabelSelect || 'SELECT SIZE';

    if (variantId) {
      btn.dataset.variantId = String(variantId);
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-disabled');
      btn.textContent = labelAdd;
      setPdprAddButtonReady(btn, true);
    } else {
      delete btn.dataset.variantId;
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
      btn.textContent = labelSelect;
      setPdprAddButtonReady(btn, false);
    }
  }

  function selectPdprSizePicker(picker) {
    const productBlock = picker.closest('product-block');
    if (!productBlock) return;
    clearPdprSizeSelection(productBlock);
    picker.classList.add('product-block-options__item--pdpr-selected');
    picker.setAttribute('aria-current', 'true');
    updatePdprAddButton(productBlock, getVariantIdFromPicker(picker));
  }

  async function addVariantToCartFromPdpr(variantId, triggerEl) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    const sections = ['page-header', 'cart-drawer'].map((s) => document.querySelector(s)).filter(Boolean);
    const sectionIds = sections.map((s) => s?.dataset?.sectionId).filter(Boolean);
    if (sectionIds.length) {
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));
    }
    const cartAddUrl = theme?.routes?.cartAdd || '/cart/add.js';
    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });
    const data = await res.json();
    const errorMessage = data.description || data.message
      || (data.errors && typeof data.errors === 'object'
        ? Object.values(data.errors).flat().join(', ')
        : null);
    if (!res.ok || data.status) {
      throw new Error(errorMessage || 'Could not add to cart');
    }

    if (data.sections && sections.length) {
      sections.forEach((el) => {
        if (el?.dataset?.sectionId && data.sections[el.dataset.sectionId]) {
          el.updateFromCartChange?.(data.sections[el.dataset.sectionId]);
        }
      });
    } else {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    }

    theme.setCartLastAddedProductId?.(data.product_id);
    document.dispatchEvent(new CustomEvent('on:cart:add', {
      bubbles: true,
      detail: { variantId: data.variant_id, productId: data.product_id }
    }));

    if (refreshCartPageAfterAdd()) {
      return data;
    }

    if (theme.settings.afterAddToCart === 'page') {
      window.location.href = theme.routes?.cart || '/cart';
      return data;
    }

    if (theme.settings.afterAddToCart === 'drawer') {
      document.querySelector('.js-cart-drawer')?.open?.(triggerEl);
    } else if (theme.settings.afterAddToCart === 'notification') {
      const notif = document.getElementById('AddedNotification')?.content?.firstElementChild?.cloneNode(true);
      if (notif) {
        notif.dataset.productTitle = data.product_title || data.title || '';
        (document.querySelector('.pageheader--sticky') || document.body).appendChild(notif);
      }
    }

    return data;
  }

  function initPdprProductBlock(productBlock) {
    if (!productBlock.classList.contains('product-block--pdpr-add-to-bag')) return;
    if (!productBlock.closest(PDPR_QUICK_ADD_ROOT)) return;
    if (!isPdprDesktop()) return;

    const btn = productBlock.querySelector('[data-pdpr-add-to-bag]');
    if (!btn || !isPdprRequiresSize(btn)) return;
    if (productBlock.dataset.pdprAddInit === 'true') return;

    productBlock.dataset.pdprAddInit = 'true';
    const pickers = getPdprSizePickers(productBlock);

    if (pickers.length === 0) {
      btn.dataset.pdprFallbackModal = 'true';
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-disabled');
      btn.textContent = btn.getAttribute('data-pdpr-label-add') || btn.dataset.pdprLabelAdd || 'ADD TO BAG';
      setPdprAddButtonReady(btn, true);
      return;
    }

    if (pickers.length === 1) {
      selectPdprSizePicker(pickers[0]);
    }
  }

  function scanPdprBlocks(root) {
    let searchRoot = root || document;
    if (root && typeof root.matches === 'function' && !root.matches(PDPR_QUICK_ADD_ROOT)) {
      const inner = root.querySelector?.(PDPR_QUICK_ADD_ROOT);
      if (inner) searchRoot = inner;
    }
    searchRoot.querySelectorAll('product-block.product-block--pdpr-add-to-bag').forEach(initPdprProductBlock);
  }

  document.addEventListener('click', async (evt) => {
    if (!isPdprDesktop()) return;

    const sizePicker = evt.target.closest(PDPR_SIZE_PICKER_SELECTOR);
    if (sizePicker) {
      evt.preventDefault();
      evt.stopPropagation();
      selectPdprSizePicker(sizePicker);
      return;
    }

    const addBtn = evt.target.closest(PDPR_ADD_BTN_SELECTOR);
    if (!addBtn || addBtn.disabled || addBtn.classList.contains('is-disabled')) return;

    evt.preventDefault();
    evt.stopPropagation();

    const productBlock = addBtn.closest('product-block');
    const handle = addBtn.dataset.productHandle || productBlock?.dataset?.productHandle;
    const productUrl = productBlock?.querySelector('.product-link')?.href;

    if (addBtn.dataset.pdprFallbackModal === 'true' && handle) {
      if (isPdprDesktop()) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          const productData = await res.json();
          if (productData.variants?.length === 1 && productData.variants[0].available) {
            addBtn.disabled = true;
            addBtn.classList.add('is-disabled');
            setPdprAddButtonReady(addBtn, false);
            const originalText = addBtn.textContent;
            addBtn.textContent = '...';
            try {
              await addVariantToCartFromPdpr(productData.variants[0].id, addBtn);
              addBtn.textContent = theme.strings?.addedToCartShort || 'Added';
              setTimeout(() => {
                addBtn.textContent = originalText;
                addBtn.disabled = false;
                addBtn.classList.remove('is-disabled');
                setPdprAddButtonReady(addBtn, true);
              }, 1500);
            } catch (err) {
              addBtn.textContent = originalText;
              addBtn.disabled = false;
              addBtn.classList.remove('is-disabled');
              setPdprAddButtonReady(addBtn, true);
              document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: err.message } }));
            }
            return;
          }
        } catch {
          /* fall through to mobile sheet */
        }
      }
      openMobileQuickAddModal(handle, productUrl, productBlock);
      return;
    }

    const variantId = addBtn.dataset.variantId || addBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    addBtn.disabled = true;
    addBtn.classList.add('is-disabled');
    setPdprAddButtonReady(addBtn, false);
    const originalText = addBtn.textContent;
    addBtn.textContent = '...';

    try {
      await addVariantToCartFromPdpr(variantId, addBtn);
      addBtn.textContent = theme.strings?.addedToCartShort || 'Added';
      setTimeout(() => {
        addBtn.textContent = originalText;
        addBtn.disabled = false;
        addBtn.classList.remove('is-disabled');
        setPdprAddButtonReady(addBtn, true);
      }, 1500);
    } catch (err) {
      addBtn.textContent = originalText;
      addBtn.disabled = false;
      addBtn.classList.remove('is-disabled');
      setPdprAddButtonReady(addBtn, true);
      document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: err.message } }));
    }
  }, true);

  function initPdprQuickAddRoots() {
    document.querySelectorAll(PDPR_QUICK_ADD_ROOT).forEach((section) => {
      scanPdprBlocks(section);
      if (section.dataset.pdprQuickAddObserved === 'true') return;
      section.dataset.pdprQuickAddObserved = 'true';
      new MutationObserver(() => scanPdprBlocks(section)).observe(section, { childList: true, subtree: true });
    });
  }

  initPdprQuickAddRoots();

  document.addEventListener('cart-cross-sell:recommendations-loaded', (evt) => {
    queueMicrotask(() => {
      const crossSell = evt.target?.matches?.('cc-cart-cross-sell') ? evt.target : evt.target?.closest?.('cc-cart-cross-sell');
      if (crossSell) scanPdprBlocks(crossSell);
      initPdprQuickAddRoots();
    });
  });

  document.addEventListener('on:cart-drawer:before-open', (evt) => {
    queueMicrotask(() => {
      evt.target?.querySelectorAll(PDPR_QUICK_ADD_ROOT).forEach((section) => {
        scanPdprBlocks(section);
      });
    });
  }, true);

  window.addEventListener('on:debounced-resize', () => {
    document.querySelectorAll(PDPR_PRODUCT_BLOCK_SELECTOR).forEach((block) => {
      delete block.dataset.pdprAddInit;
      clearPdprSizeSelection(block);
      const btn = block.querySelector('[data-pdpr-add-to-bag]');
      if (!btn || !isPdprRequiresSize(btn)) return;
      delete btn.dataset.pdprFallbackModal;
      delete btn.dataset.variantId;
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
      btn.textContent = btn.getAttribute('data-pdpr-label-select') || btn.dataset.pdprLabelSelect || 'SELECT SIZE';
      setPdprAddButtonReady(btn, false);
    });
    if (isPdprDesktop()) {
      document.querySelectorAll(PDPR_QUICK_ADD_ROOT).forEach((section) => scanPdprBlocks(section));
    }
  });
})();

/* ==========================================================================
  PLP + MOBILE QUICK-ADD: Notify me / View similar actions
  ========================================================================== */
(function() {
  'use strict';

  const KLAVIYO_BIS_TRIGGER_SELECTOR =
    'a.klaviyo-bis-trigger, button.klaviyo-bis-trigger, .klaviyo-bis-anchor .klaviyo-bis-trigger, .klaviyo-bis-anchor button';

  function findKlaviyoBisTrigger(root) {
    return root?.querySelector(KLAVIYO_BIS_TRIGGER_SELECTOR) || null;
  }

  function closeMobileQuickAddModal() {
    const modal = document.getElementById('mobile-quick-add-modal');
    if (!modal?.classList.contains('is-open')) return;
    modal.classList.remove('is-open', 'mobile-quick-add-modal--from-cart-drawer');
    modal.setAttribute('aria-hidden', 'true');
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    document.body.style.overflow = '';
  }

  function getVariantIdFromModal(modal) {
    if (!modal) return null;
    const atc = modal.querySelector('.mobile-quick-add-modal__atc');
    const variantId = atc?.dataset?.variantId;
    return variantId ? parseInt(variantId, 10) : null;
  }

  function triggerRestockNotify(productHandle, variantId) {
    const baseUrl = `/products/${encodeURIComponent(productHandle)}`;
    const params = new URLSearchParams();
    if (variantId) params.set('variant', String(variantId));
    params.set('notify', '1');
    window.location.href = `${baseUrl}?${params.toString()}`;
  }

  async function addVariantToCartFromPlp(triggerEl, variantId) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    const sections = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter(Boolean);
    const sectionIds = sections.map((el) => el?.dataset?.sectionId).filter(Boolean);
    if (sectionIds.length) {
      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sectionIds.join(','));
    }
    const cartAddUrl = theme?.routes?.cartAdd || '/cart/add.js';
    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });
    const data = await res.json();
    const errorMessage = data.description || data.message
      || (data.errors && typeof data.errors === 'object'
        ? Object.values(data.errors).flat().join(', ')
        : null);
    if (!res.ok || data.status) {
      throw new Error(errorMessage || 'Could not add to cart');
    }

    if (data.sections && sections.length) {
      sections.forEach((el) => {
        if (el?.dataset?.sectionId && data.sections[el.dataset.sectionId]) {
          el.updateFromCartChange?.(data.sections[el.dataset.sectionId]);
        }
      });
    } else {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    }

    theme.setCartLastAddedProductId?.(data.product_id);
    document.dispatchEvent(new CustomEvent('on:cart:add', {
      bubbles: true,
      detail: { variantId: data.variant_id, productId: data.product_id }
    }));

    if (theme.settings?.afterAddToCart === 'page') {
      window.location.href = theme.routes?.cart || '/cart';
      return data;
    }

    if (theme.settings?.afterAddToCart === 'drawer') {
      document.querySelector('.js-cart-drawer')?.open?.(triggerEl);
    } else if (theme.settings?.afterAddToCart === 'notification') {
      const notif = document.getElementById('AddedNotification')?.content?.firstElementChild?.cloneNode(true);
      if (notif) {
        notif.dataset.productTitle = data.product_title || data.title || '';
        (document.querySelector('.pageheader--sticky') || document.body).appendChild(notif);
      }
    }

    return data;
  }

  function isNativeKlaviyoBisTrigger(btn) {
    // Klaviyo injects and binds BIS only inside PDP anchors — not on collection tiles.
    return !!btn.closest('.klaviyo-bis-anchor')
      && btn.classList.contains('klaviyo-bis-trigger');
  }

  document.addEventListener('click', (evt) => {
    const preorderAtcBtn = evt.target.closest('[data-plp-preorder-add-to-bag]');
    if (preorderAtcBtn) {
      evt.preventDefault();
      evt.stopPropagation();
      const variantId = preorderAtcBtn.dataset.variantId;
      if (!variantId || preorderAtcBtn.disabled) return;

      const originalText = preorderAtcBtn.textContent;
      preorderAtcBtn.disabled = true;
      preorderAtcBtn.textContent = '...';

      addVariantToCartFromPlp(preorderAtcBtn, variantId)
        .then(() => {
          preorderAtcBtn.textContent = theme.strings?.addedToCartShort || 'Added';
          window.setTimeout(() => {
            preorderAtcBtn.textContent = originalText;
            preorderAtcBtn.disabled = false;
          }, 1500);
        })
        .catch((err) => {
          preorderAtcBtn.textContent = originalText;
          preorderAtcBtn.disabled = false;
          document.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: err.message }
          }));
        });
      return;
    }

    const notifyBtn = evt.target.closest('[data-plp-notify-when-available]');
    if (notifyBtn) {
      const productBlock = notifyBtn.closest('product-block, [data-blog-look-product]');
      if (productBlock?.dataset?.preorderAvailability === 'true') {
        return;
      }

      const modal = notifyBtn.closest('.mobile-quick-add-modal');
      closeMobileQuickAddModal();

      if (isNativeKlaviyoBisTrigger(notifyBtn)) {
        return;
      }

      evt.preventDefault();
      evt.stopPropagation();
      const handle = notifyBtn.dataset.productHandle;
      if (!handle) return;
      const variantId = notifyBtn.dataset.variantId
        ? parseInt(notifyBtn.dataset.variantId, 10)
        : getVariantIdFromModal(modal);
      triggerRestockNotify(handle, variantId);
      return;
    }

    const viewSimilarBtn = evt.target.closest('[data-plp-view-similar]');
    if (viewSimilarBtn) {
      evt.stopPropagation();
      closeMobileQuickAddModal();
    }
  }, true);
})();

/* ==========================================================================
  UPC Tooltip on Product Card Hover
  Shows the UPC code when hovering over product images (desktop only)
  Tooltip appears at cursor position after delay and stays static until hover ends
   ========================================================================== */
(function() {
  'use strict';

  // Skip on touch devices
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    return;
  }

  // Configuration
  const HOVER_DELAY = 700; // 1.5 seconds before showing tooltip

  // State
  let tooltip = null;
  let hoverTimer = null;
  let currentTarget = null;
  let mouseX = 0;
  let mouseY = 0;

  function createTooltip() {
    if (tooltip) return tooltip;
    tooltip = document.createElement('div');
    tooltip.className = 'upc-tooltip';
    tooltip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tooltip);
    return tooltip;
  }

  function showTooltip(upc) {
    if (!tooltip) createTooltip();
    tooltip.textContent = upc;
    // Position at current cursor location (stays fixed after appearing)
    tooltip.style.left = mouseX + 'px';
    tooltip.style.top = mouseY + 'px';
    // Force reflow before adding class for transition
    tooltip.offsetHeight;
    tooltip.classList.add('is-visible');
  }

  function hideTooltip() {
    if (tooltip) {
      tooltip.classList.remove('is-visible');
    }
    clearTimeout(hoverTimer);
    hoverTimer = null;
    currentTarget = null;
  }

  function handleMouseEnter(e) {
    if (e.target.closest('.image-page-button')) {
      hideTooltip();
      return;
    }

    // Support both product cards (.image-cont) and product page media (.product-media-wrapper)
    const imageCont = e.target.closest('.image-cont[data-upc], .product-media-wrapper[data-upc]');
    if (!imageCont) return;

    const upc = imageCont.dataset.upc;
    if (!upc) return;

    currentTarget = imageCont;
    
    // Clear any existing timer
    clearTimeout(hoverTimer);
    
    // Start delay timer - tooltip will appear at cursor position when timer fires
    hoverTimer = setTimeout(function() {
      if (currentTarget === imageCont) {
        showTooltip(upc);
      }
    }, HOVER_DELAY);
  }

  function handleMouseLeave(e) {
    // Support both product cards (.image-cont) and product page media (.product-media-wrapper)
    const imageCont = e.target.closest('.image-cont[data-upc], .product-media-wrapper[data-upc]');
    if (!imageCont) return;

    // Check if we're leaving the image container entirely
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && imageCont.contains(relatedTarget)) {
      return; // Still within the same container
    }

    hideTooltip();
  }

  function handleMouseMove(e) {
    // Only track position while tooltip is NOT visible (before it appears)
    // Once visible, position stays fixed
    if (!tooltip || !tooltip.classList.contains('is-visible')) {
      mouseX = e.clientX;
      mouseY = e.clientY;
    }
  }

  // Use event delegation for better performance
  function init() {
    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
  Back to Top Button
  Shows/hides button based on scroll position and handles smooth scroll to top
  ========================================================================== */
(function() {
  'use strict';

  // Configuration
  const SCROLL_THRESHOLD = 400; // Show button after scrolling 400px
  const SCROLL_DURATION = 600; // Smooth scroll duration in ms

  let backToTopButton = null;
  let scrollTimeout = null;
  let isScrolling = false;

  function init() {
    backToTopButton = document.querySelector('.back-to-top');
    if (!backToTopButton) return;

    // Handle scroll events
    let ticking = false;
    function handleScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateButtonVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }

    // Throttled scroll handler
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    updateButtonVisibility();

    // Handle button click
    backToTopButton.addEventListener('click', scrollToTop);
    
    // Handle keyboard navigation
    backToTopButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        scrollToTop();
      }
    });
  }

  function updateButtonVisibility() {
    if (!backToTopButton) return;

    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    const shouldShow = scrollY > SCROLL_THRESHOLD;

    if (shouldShow) {
      backToTopButton.classList.add('is-visible');
      backToTopButton.setAttribute('aria-hidden', 'false');
    } else {
      backToTopButton.classList.remove('is-visible');
      backToTopButton.setAttribute('aria-hidden', 'true');
    }
  }

  function scrollToTop() {
    if (isScrolling) return;
    isScrolling = true;

    const startY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    const startTime = performance.now();

    function animateScroll(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / SCROLL_DURATION, 1);
      
      // Easing function (ease-in-out-cubic)
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const currentY = startY * (1 - ease);
      window.scrollTo(0, currentY);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        isScrolling = false;
        // Update button visibility after scroll completes
        updateButtonVisibility();
      }
    }

    requestAnimationFrame(animateScroll);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Swym PLP collection hearts: remove inline translate() Swym applies from product-link
 * width math (otherwise the button lands on the neighbouring tile).
 */
(function() {
  function fixSwymCollectionHearts(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.image-cont [class*="swym-wishlist"][class*="container"] button, .image-cont button[class*="swym-wishlist-collections"]').forEach(function(btn) {
      if (btn.style.transform) btn.style.removeProperty('transform');
      if (btn.style.webkitTransform) btn.style.removeProperty('-webkit-transform');
    });
  }

  function scheduleFix() {
    fixSwymCollectionHearts(document);
    setTimeout(function() { fixSwymCollectionHearts(document); }, 300);
    setTimeout(function() { fixSwymCollectionHearts(document); }, 1200);
  }

  function watchCollectionHearts() {
    var listing = document.querySelector('.collection-listing, .template-search .collection-listing');
    if (!listing || listing.getAttribute('data-swym-hearts-bound') === '1') return;
    listing.setAttribute('data-swym-hearts-bound', '1');
    var mo = new MutationObserver(function() { fixSwymCollectionHearts(listing); });
    mo.observe(listing, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      scheduleFix();
      watchCollectionHearts();
    });
  } else {
    scheduleFix();
    watchCollectionHearts();
  }

  document.addEventListener('on:cart:after-merge', function() {
    scheduleFix();
  });
})();

/**
 * PDP: Change "Buy it now" to "Buy it Now" on dynamic payment button (Shopify injects it)
 */
(function() {
  function updateBuyItNowText() {
    document.querySelectorAll('.shopify-payment-button__button--unbranded, [data-testid="ShopifyPay-button"], .shopify-payment-button__button').forEach(function(btn) {
      if (btn.textContent.trim() === 'Buy it now') {
        btn.textContent = 'Buy it Now';
      }
    });
  }
  function init() {
    updateBuyItNowText();
    var containers = document.querySelectorAll('.product-dynamic-payment-button, .quantity-submit-row');
    if (containers.length) {
      var obs = new MutationObserver(updateBuyItNowText);
      containers.forEach(function(el) { obs.observe(el, { childList: true, subtree: true }); });
    }
    setTimeout(updateBuyItNowText, 500);
    setTimeout(updateBuyItNowText, 1500);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * PDP Media Gallery: mirror the main-image's actual rendered height onto the
 * gallery element as --pdp-main-image-height, so the vertical thumbnail column
 * (in the "carousel-beside" layout, >=1041px) can cut off exactly at the image
 * bottom edge instead of the .main-image container's bottom.
 *
 * Why: the .main-image container includes a margin-bottom + the height
 * reserved by carousel-slider's data-fixed-height mode (which pads short slides
 * to match the first/reference slide). Without this, CSS can only clamp to the
 * grid cell height, which over-extends below the visible image on viewports
 * where the image is heavily scaled down.
 *
 * The carousel-slider web component already computes exactly the number we
 * want and writes it to its own --current-slide-height (see main.js
 * updateFixedHeight / updateDynamicHeight). We just relay it up to the gallery
 * ancestor so a sibling (.thumbnails) can inherit it. The CSS fallback (100%)
 * keeps the prior behavior if JS is disabled or slow.
 */
(function () {
  function mirrorCarouselHeight(gallery) {
    if (!gallery || gallery.dataset.thumbHeightSyncBound === '1') return;
    var mainImage = gallery.querySelector('.main-image');
    if (!mainImage) return;
    var carousel = mainImage.querySelector('carousel-slider');
    if (!carousel) return;

    gallery.dataset.thumbHeightSyncBound = '1';

    function read() {
      // Prefer the var the carousel itself sets; fall back to measuring the
      // first slide's first child (same thing the carousel measures internally).
      var fromVar = carousel.style.getPropertyValue('--current-slide-height').trim();
      if (fromVar) return fromVar;
      var firstSlide = carousel.querySelector('.slider__item');
      var firstEl = firstSlide && firstSlide.firstElementChild;
      if (firstEl && firstEl.clientHeight > 0) return firstEl.clientHeight + 'px';
      return '';
    }

    function sync() {
      var h = read();
      if (h) gallery.style.setProperty('--pdp-main-image-height', h);
    }

    sync();
    // Initial settle: images/fonts may not be measured on first rAF.
    requestAnimationFrame(sync);
    setTimeout(sync, 150);
    setTimeout(sync, 600);

    // React to the carousel writing/updating its own height var.
    try {
      var styleObs = new MutationObserver(sync);
      styleObs.observe(carousel, { attributes: true, attributeFilter: ['style'] });
    } catch (e) { /* noop */ }

    // Direct size changes (rare, but safe belt-and-braces for browsers where
    // the carousel doesn't re-emit a style mutation on resize).
    if (typeof ResizeObserver !== 'undefined') {
      try {
        var ro = new ResizeObserver(sync);
        ro.observe(carousel);
        var firstSlide = carousel.querySelector('.slider__item');
        if (firstSlide && firstSlide.firstElementChild) ro.observe(firstSlide.firstElementChild);
      } catch (e) { /* noop */ }
    }

    window.addEventListener('resize', sync, { passive: true });
    window.addEventListener('load', sync, { once: true });
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.media-gallery.media-gallery--layout-carousel-beside.has-thumbnails')
      .forEach(mirrorCarouselHeight);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-run for dynamically loaded sections (section editor, AJAX product swaps).
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) initAll(e.target);
  });
})();

/**
 * Wishlist toast: Swym’s blue bar / native UI stays hidden. We show a small
 * designers-styled toast (top-right) for add + remove instead.
 */
(function () {
  if (window.__aesthetWishlistToastInit) return;
  window.__aesthetWishlistToastInit = true;

  var SEL = 'swym-storefront-layout-notification#swym-storefront-layout-notification';
  var TOAST_ID = 'aesthet-wishlist-toast';
  var HIDE_MS = 2000;
  var watched = null;
  var queued = false;
  var hideTimer = null;
  var lastShownAt = 0;

  function copyFor(isRemoved) {
    var lang = (document.documentElement.lang || 'en').toLowerCase();
    if (lang.indexOf('el') === 0) {
      return isRemoved
        ? { title: 'Λίστα αγαπημένων', message: 'Αφαιρέθηκε από τη λίστα αγαπημένων.' }
        : { title: 'Λίστα αγαπημένων', message: 'Προστέθηκε σε λίστα αγαπημένων.' };
    }
    if (lang.indexOf('de') === 0) {
      return isRemoved
        ? { title: 'Wunschliste', message: 'Artikel von der Wunschliste entfernt.' }
        : { title: 'Wunschliste', message: 'Artikel zur Wunschliste hinzugefügt.' };
    }
    return isRemoved
      ? { title: 'Wishlist', message: 'Item removed from wishlist.' }
      : { title: 'Wishlist', message: 'Item saved to wishlist.' };
  }

  function unlockScroll() {
    if (document.querySelector('.wishlist-login-modal.is-open')) return;
    if (document.querySelector('.swym-modal.swym-sfl-popup')) return;
    document.body.classList.remove('swym-no-scroll', 'swym-modal-open', 'swym-overlay-open');
    document.documentElement.classList.remove('swym-no-scroll', 'swym-modal-open', 'swym-overlay-open');
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
  }

  function scheduleUnlock() {
    unlockScroll();
    setTimeout(unlockScroll, 100);
    setTimeout(unlockScroll, 400);
  }

  function ensureToast() {
    var el = document.getElementById(TOAST_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = TOAST_ID;
    el.className = 'aesthet-wishlist-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML =
      '<span class="aesthet-wishlist-toast__title"></span>' +
      '<span class="aesthet-wishlist-toast__message"></span>';
    document.body.appendChild(el);
    return el;
  }

  function showToast(title, message) {
    var now = Date.now();
    var fp = title + '|' + message;
    /* Block only the same message repeating — allow saved → removed back-to-back */
    if (
      fp === window.__aesthetWishlistToastFp &&
      now - (window.__aesthetWishlistToastAt || 0) < 2500
    ) {
      return;
    }
    window.__aesthetWishlistToastFp = fp;
    window.__aesthetWishlistToastAt = now;
    lastShownAt = now;

    var el = ensureToast();
    var titleEl = el.querySelector('.aesthet-wishlist-toast__title');
    var msgEl = el.querySelector('.aesthet-wishlist-toast__message');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = message;
    el.classList.add('is-visible');

    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, HIDE_MS);

    scheduleUnlock();
  }

  window.aesthetShowWishlistToast = function (isRemoved) {
    var copy = copyFor(!!isRemoved);
    showToast(copy.title, copy.message);
  };

  function handleSwymNotification(el) {
    if (!el || !el.childElementCount) return;
    /* Never toast from Swym UI — theme heart handlers own the toast.
       Only unlock scroll so the hidden blue bar cannot freeze the page. */
    scheduleUnlock();
  }

  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      var el = document.querySelector(SEL);
      if (!el) return;
      watch(el);
      handleSwymNotification(el);
    });
  }

  var notifMo = new MutationObserver(queue);

  function watch(el) {
    if (!el || watched === el) return;
    if (watched) notifMo.disconnect();
    watched = el;
    notifMo.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['status', 'class'],
    });
  }

  var bodyMo = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var nodes = mutations[i].addedNodes;
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (!n || n.nodeType !== 1) continue;
        if (n.matches && (n.matches(SEL) || (n.querySelector && n.querySelector(SEL)))) {
          queue();
          return;
        }
      }
    }
  });

  function start() {
    queue();
    if (document.body) bodyMo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Swym header wishlist + toast "View" — route to theme wishlist page (not Swym default UI).
 */
(function() {
  const SWYM_HEADER_WISHLIST_SEL = '#swym-advanced-header-icon';
  const SWYM_HEADER_WISHLIST_CLICK_SEL =
    '#swym-advanced-header-icon, ' +
    '.swym-advanced-header-icon, ' +
    '.swym-header-icon-wrapper, ' +
    '[data-header-wishlist-placeholder], ' +
    '.logo-area__right__inner .wishlist-page-link, ' +
    '.mobile-drawer-account-links .wishlist-page-link';
  const SWYM_NOTIFICATION_SEL = 'swym-storefront-layout-notification, #swym-storefront-layout-notification';
  const SWYM_NOTIFICATION_ACTION_SEL = '.swym-storefront-layout-notification-action';
  const SWYM_LIST_HASH = '#swym-list';
  const HEADER_ICONS_ROOT = '.logo-area__right__inner';
  const READY_CLASS = 'swym-header-wishlist-ready';

  function getWishlistPageUrl() {
    return document.body.getAttribute('data-wishlist-page-url') || '/pages/wishlist';
  }

  function shouldRedirectSwymNotificationView(btn, notif) {
    if (!btn || !notif) return false;

    const label = (btn.textContent || '').trim().toLowerCase();
    if (!label) return false;
    if (/cart|checkout|bag|collection/.test(label)) return false;

    return !!(
      notif.querySelector('img') ||
      notif.querySelector('[class*="notification-title"]') ||
      notif.querySelector('[class*="notification-image"]')
    );
  }

  function redirectToWishlistPage() {
    window.location.assign(getWishlistPageUrl());
  }

  function redirectSwymListHash() {
    if (window.location.hash !== SWYM_LIST_HASH) return;
    history.replaceState(null, '', window.location.pathname + window.location.search);
    redirectToWishlistPage();
  }

  function swymHeaderIconPresent() {
    const root = document.querySelector(HEADER_ICONS_ROOT);
    return !!(root && root.querySelector(SWYM_HEADER_WISHLIST_SEL));
  }

  function markSwymHeaderReady() {
    if (!swymHeaderIconPresent()) return false;
    document.body.classList.add(READY_CLASS);
    return true;
  }

  function watchForSwymHeaderIcon() {
    if (markSwymHeaderReady()) return;

    const root = document.querySelector(HEADER_ICONS_ROOT);
    if (!root) return;

    const mo = new MutationObserver(function() {
      markSwymHeaderReady();
    });
    mo.observe(root, { childList: true, subtree: true });

    [50, 150, 400, 800, 1500, 3000, 6000].forEach(function(ms) {
      setTimeout(markSwymHeaderReady, ms);
    });
  }

  document.addEventListener(
    'click',
    function(e) {
      const headerEl =
        e.target && e.target.closest && e.target.closest(SWYM_HEADER_WISHLIST_CLICK_SEL);
      if (headerEl) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        redirectToWishlistPage();
        return;
      }

      const actionBtn =
        e.target && e.target.closest && e.target.closest(SWYM_NOTIFICATION_ACTION_SEL);
      if (!actionBtn) return;

      const notif = actionBtn.closest(SWYM_NOTIFICATION_SEL);
      if (!shouldRedirectSwymNotificationView(actionBtn, notif)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      redirectToWishlistPage();
    },
    true
  );

  window.addEventListener('hashchange', redirectSwymListHash);
  redirectSwymListHash();

  document.addEventListener('visitorConsentCollected', function() {
    setTimeout(markSwymHeaderReady, 100);
    setTimeout(watchForSwymHeaderIcon, 100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForSwymHeaderIcon);
  } else {
    watchForSwymHeaderIcon();
  }
})();

/**
 * Wishlist Login Modal
 * When non-logged-in users click a wishlist control, show a login/register modal
 * instead of letting the wishlist app handle it (mirrors designers page behavior).
 */
(function() {
  function isHeaderWishlistLauncher(el) {
    if (!el || typeof el.closest !== 'function') return false;
    return !!el.closest('.wishlist-page-link, #swym-advanced-header-icon');
  }

  function init() {
    const modal = document.getElementById('wishlist-login-modal');
    const closeBtn = document.querySelector('[data-wishlist-login-modal-close]');
    if (!modal) return;

    const isLoggedIn = document.body.getAttribute('data-customer-logged-in') === 'true';

    function isLikelyWishlistControl(el) {
      if (!el || !(el instanceof Element)) return false;

      /* PDP text/outline button is intentionally wider than collection hearts. */
      if (el.id === 'swym-atw-pdp-button' || el.closest('#swym-atw-pdp-button')) return true;
      if (el.closest('[data-pdp-wishlist-toggle]')) return true;

      if (typeof el.getBoundingClientRect !== 'function') return true;

      var rect = el.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return false;

      /* Reject full-width / page-sized invisible Swym shells on collection tiles. */
      if (rect.width > 100 || rect.height > 100) return false;

      return true;
    }

    function getWishlistTrigger(target) {
      if (!target || typeof target.closest !== 'function') return null;

      /* Never treat modal chrome (close, backdrop, CTAs) as a wishlist heart tap. */
      if (target.closest('#wishlist-login-modal, .wishlist-login-modal')) return null;

      if (isHeaderWishlistLauncher(target)) return null;

      var trigger = target.closest(
        '#swym-atw-pdp-button, ' +
          '[data-pdp-wishlist-toggle], ' +
          '[data-plp-wishlist-toggle], ' +
          '[data-cart-swym-wishlist], ' +
          '.image-cont button[class*="swym-wishlist"], ' +
          '.image-cont button[class*="swym-advanced-wishlist"], ' +
          '.image-cont [class*="swym-wishlist"] [role="button"], ' +
          '.image-cont [class*="swym-advanced-wishlist"] [role="button"], ' +
          '.image-cont button.swym-wishlist-collections-v2, ' +
          '.image-cont button[data-swaction="addToWishlist"], ' +
          '.image-cont button[data-swaction="addToWishList"]'
      );

      if (!trigger || !isLikelyWishlistControl(trigger)) return null;

      return trigger;
    }

    function openModal() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function stopEvent(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }

    // Intercept wishlist clicks when not logged in (capture phase runs before app handlers)
    document.addEventListener('click', function(e) {
      if (modal.classList.contains('is-open')) return;

      const wishlistEl = getWishlistTrigger(e.target);
      if (!wishlistEl || isLoggedIn) return;

      stopEvent(e);
      openModal();
    }, true);

    if (closeBtn) {
      closeBtn.addEventListener('click', function(e) {
        stopEvent(e);
        closeModal();
      }, true);
    }

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        stopEvent(e);
        closeModal();
      }
    }, true);

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


(function() {
  // Add custom code below this line

  var MOBILE_FORMS_TEASER_MQ = '(max-width: 767.98px)';
  /* Below mobile .page-shade/.header-shade (7000), filter shade (8001), drawer (8001), filters panel (8002) */
  var FORMS_TEASER_Z_UNDER_OVERLAYS = '6900';
  var FORMS_TEASER_Z_ABOVE_CHROME = '2147483647';

  function isMobileFormsTeaserViewport() {
    return window.matchMedia(MOBILE_FORMS_TEASER_MQ).matches;
  }

  /* Modal open = raise embed above sticky header (8000); closed = keep teaser stack low (6900). */
  function isShopifyFormsModalOpen() {
    function isVisibleLayer(el) {
      if (!el || !(el instanceof Element)) return false;
      if (typeof el.getBoundingClientRect !== 'function') return false;
      var r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return false;
      if (el.closest && el.closest('[hidden]')) return false;
      var ah = el.getAttribute && el.getAttribute('aria-hidden');
      if (ah === 'true') return false;
      var ds = el.getAttribute && el.getAttribute('data-state');
      if (ds === 'closed') return false;
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      var op = parseFloat(cs.opacity);
      if (!isNaN(op) && op < 0.05) return false;
      return true;
    }

    function scan(root) {
      if (!root || !root.querySelectorAll) return false;
      var nodes = root.querySelectorAll('[role="dialog"], dialog[open], [aria-modal="true"]');
      for (var i = 0; i < nodes.length; i++) {
        if (isVisibleLayer(nodes[i])) return true;
      }
      return false;
    }

    var ae = document.getElementById('app-embed');
    if (ae && scan(ae)) return true;
    var hosts = document.querySelectorAll('shopify-forms-embed, form-embed');
    for (var h = 0; h < hosts.length; h++) {
      if (hosts[h].shadowRoot && scan(hosts[h].shadowRoot)) return true;
    }
    var router = document.querySelector('[data-testid="app-router"]');
    if (router && scan(router)) return true;
    return false;
  }

  var formsShadowObserversBound = new WeakSet();
  function observeFormsShadowRootsForModal() {
    document.querySelectorAll('shopify-forms-embed, form-embed').forEach(function (host) {
      var sr = host.shadowRoot;
      if (!sr || formsShadowObserversBound.has(sr)) return;
      formsShadowObserversBound.add(sr);
      var mo = new MutationObserver(scheduleShopifyFormsTeaserSync);
      mo.observe(sr, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'data-state', 'open', 'hidden', 'style'],
        subtree: true,
        childList: true
      });
    });
  }

  function formsTeaserButtons() {
    /* Only true Shopify Forms teaser buttons — never button[aria-haspopup="dialog"] (matches modal UI and breaks after open). */
    var sel =
      'button[class*="teaserContainer"], button[class*="TeaserContainer"], button._teaserContainer_trejf_5';
    var seen = new Set();
    var list = [];

    function add(btn) {
      if (!btn || seen.has(btn)) return;
      seen.add(btn);
      list.push(btn);
    }

    var appEmbed = document.getElementById('app-embed');
    if (appEmbed) {
      appEmbed.querySelectorAll(sel).forEach(add);
    }

    document.querySelectorAll('shopify-forms-embed, form-embed').forEach(function (host) {
      if (host.shadowRoot) {
        host.shadowRoot.querySelectorAll(sel).forEach(add);
      }
    });

    var router = document.querySelector('[data-testid="app-router"]');
    if (router) router.querySelectorAll(sel).forEach(add);

    return list;
  }

  function syncShopifyFormsTeaserMobileLayer() {
    observeFormsShadowRootsForModal();
    var mobile = isMobileFormsTeaserViewport();
    var onProduct = document.body.classList.contains('template-product');
    var modalOpen = isShopifyFormsModalOpen();
    /* Mobile non-PDP: teaser/embed wrapper stays low unless form modal is open (modal must clear header). */
    var stackBelowThemeChrome = mobile && !onProduct && !modalOpen;

    formsTeaserButtons().forEach(function (btn) {
      if (mobile && onProduct) {
        btn.setAttribute('data-aesthet-forms-teaser-suppressed', '');
        btn.style.setProperty('display', 'none', 'important');
        btn.style.setProperty('visibility', 'hidden', 'important');
        btn.style.setProperty('pointer-events', 'none', 'important');
      } else {
        if (btn.hasAttribute('data-aesthet-forms-teaser-suppressed')) {
          btn.removeAttribute('data-aesthet-forms-teaser-suppressed');
          btn.style.removeProperty('display');
          btn.style.removeProperty('visibility');
          btn.style.removeProperty('pointer-events');
        }
        btn.style.setProperty(
          'z-index',
          stackBelowThemeChrome ? FORMS_TEASER_Z_UNDER_OVERLAYS : FORMS_TEASER_Z_ABOVE_CHROME,
          'important'
        );
      }
    });

    var appEmbed = document.getElementById('app-embed');
    if (appEmbed) {
      if (mobile && onProduct) {
        appEmbed.setAttribute('data-aesthet-forms-teaser-suppressed', '');
        appEmbed.style.setProperty('display', 'none', 'important');
      } else {
        if (appEmbed.hasAttribute('data-aesthet-forms-teaser-suppressed')) {
          appEmbed.removeAttribute('data-aesthet-forms-teaser-suppressed');
          appEmbed.style.removeProperty('display');
        }
        appEmbed.style.setProperty(
          'z-index',
          stackBelowThemeChrome ? FORMS_TEASER_Z_UNDER_OVERLAYS : FORMS_TEASER_Z_ABOVE_CHROME,
          'important'
        );
      }
    }

    document.querySelectorAll('shopify-forms-embed, form-embed').forEach(function (host) {
      if (mobile && onProduct) {
        host.setAttribute('data-aesthet-forms-teaser-suppressed', '');
        host.style.setProperty('display', 'none', 'important');
      } else {
        if (host.hasAttribute('data-aesthet-forms-teaser-suppressed')) {
          host.removeAttribute('data-aesthet-forms-teaser-suppressed');
          host.style.removeProperty('display');
        }
        if (mobile && !onProduct && modalOpen) {
          host.style.setProperty('z-index', FORMS_TEASER_Z_ABOVE_CHROME, 'important');
          host.style.setProperty('position', 'relative', 'important');
        } else {
          host.style.removeProperty('z-index');
          host.style.removeProperty('position');
        }
      }
    });
  }

  var formsTeaserSyncRaf = null;
  function scheduleShopifyFormsTeaserSync() {
    if (formsTeaserSyncRaf) return;
    formsTeaserSyncRaf = requestAnimationFrame(function () {
      formsTeaserSyncRaf = null;
      syncShopifyFormsTeaserMobileLayer();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    syncShopifyFormsTeaserMobileLayer();
    window.matchMedia(MOBILE_FORMS_TEASER_MQ).addEventListener('change', syncShopifyFormsTeaserMobileLayer);
    var teaserMo = new MutationObserver(scheduleShopifyFormsTeaserSync);
    teaserMo.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'data-state', 'open', 'hidden', 'style'],
      subtree: true,
      childList: true
    });
  });

 document.addEventListener("DOMContentLoaded", function () {
    const rteDivs = document.querySelectorAll('.rte');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 
    rteDivs.forEach(rte => {
      const paragraphs = rte.querySelectorAll('p');
 
      paragraphs.forEach(p => {
        if (p.querySelector('a[href^="mailto:"], svg, img')) return;

        const content = p.textContent.trim();
 
        // Plain-text email paragraphs only (skip rich contact rows with icons)
        if (emailRegex.test(content) && p.children.length === 0) {
          const a = document.createElement('a');
          a.href = `mailto:${content}`;
          a.textContent = content;
          a.style.textDecoration = "none";
          a.style.borderBottom = "1px solid white";
          a.style.paddingBottom = "2px";
          a.style.color = "white";
         
     
          p.innerHTML = '';
          p.appendChild(a);
        }
      });
    });
  });
 
 
  document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver(() => {
    // Support both old <form-embed> and new <shopify-forms-embed>
    const formHost = document.querySelector('shopify-forms-embed, form-embed');
 
    if (formHost && formHost.shadowRoot) {
      observer.disconnect();
 
      const shadow = formHost.shadowRoot;
 
      const styleSheet = new CSSStyleSheet();
      styleSheet.replaceSync(`
        /* Teaser Button - Updated for new class structure */
        button._teaserContainer_trejf_5 {
          all: unset !important;
          position: fixed !important;
          top: 50% !important;
          left: 0px !important;
          transform: translateY(-74%)!important;
          transform-origin: top left !important;
          background-color: #F9F7F4 !important;
          padding: 12px 14px !important;
          font-size: 14px !important;
          line-height: 100% !important;
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
          text-transform: uppercase !important;
          color: rgb(0, 0, 0) !important;
          cursor: pointer !important;
          z-index: 2147483647 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 12px !important;
          border: medium !important;
          box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 8px !important;
          min-width: 90px !important;
        }
       
        /* Teaser Message - Updated for new class structure */
        h3._teaserHeading_trejf_28 {
          all: unset !important;
          color: rgb(0, 0, 0) !important;
          font-size: 14px !important;
          font-weight: 300 !important;
          font-family: "Noto Sans", sans-serif !important;
          text-transform: uppercase !important;
        }
 
        /* Close Button - Updated for new class structure */
        span._formCloseButton_1684x_4 {
          all: unset !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 14px !important;
          height: 14px !important;
          border-radius: 50% !important;
          border: 1px solid rgb(0, 0, 0) !important;
          background-color: #fff !important;
          cursor: pointer !important;
        }
       
        /* Make the X icon smaller */
        span._formCloseButton_1684x_4 svg {
          width: 6px !important;
          height: 6px !important;
        }
 
        span._formCloseButton_1684x_4:hover {
          color: red !important;
        }
 
        @media screen and (max-width: 768px) {
          button._teaserContainer_trejf_5 {
            font-size: 14px !important;
            padding-top: 54px !important;
            min-width: 100px !important;
            z-index: 6900 !important;
          }
 
          span._formCloseButton_1684x_4 svg {
            width: 10px !important;
            height: 10px !important;
          }
 
          span._formCloseButton_1684x_4 {
            all: unset !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 18px !important;
            height: 18px !important;
            border-radius: 50% !important;
            border: 1px solid rgb(0, 0, 0) !important;
            background-color: #fff !important;
            cursor: pointer !important;
          }
        }
     
        h2._textHeading_2aowh_35 {
          color: #000;
          font-family: "Noto Sans", sans-serif;
          font-size: 24px;
          font-style: normal;
          font-weight: 300;
          line-height: 100%;
        }
       
        input._formInputField_237zm_7{
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
        }

        input._formInputField_237zm_7::placeholder{
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
        }
 
        ._textBody_2aowh_10, span._textBody_2aowh_10, p._textBody_2aowh_10{
            color: #000;
            font-family: "Noto Sans", sans-serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 136%;
        }
       
        button._formSubmitButton_8skcf_90,
        button[class*="formSubmitButton"] {
          color: #fff !important;
          font-family: "Noto Sans", sans-serif;
          font-size: 16px;
          font-style: normal;
          font-weight: 300;
          line-height: 100%;
          background-color: #84714c;
          border-radius: 0px;
        }

        button._formSubmitButton_8skcf_90,
        button._formSubmitButton_8skcf_90 *,
        button[class*="formSubmitButton"],
        button[class*="formSubmitButton"] * {
          color: #fff !important;
          -webkit-text-fill-color: #fff !important;
        }

        label[class*="radio"],
        [class*="radio"] label,
        [class*="choice"] label,
        [class*="choiceLabel"],
        [class*="radio"] span[class*="textBody"],
        [class*="choice"] span[class*="textBody"] {
          font-size: 14px !important;
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
        }
       
        div._formDisclaimer_8skcf_38{
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
        }
 
        /* Additional selectors for better coverage */
        button[class*="teaserContainer"] {
          all: unset !important;
          position: fixed !important;
          top: 50% !important;
          left: 0px !important;
          transform: translateY(-74%)!important;
          transform-origin: top left !important;
          background-color: #F9F7F4 !important;
          padding: 12px 14px !important;
          font-size: 14px !important;
          line-height: 100% !important;
          font-family: "Noto Sans", sans-serif !important;
          font-weight: 300 !important;
          text-transform: uppercase !important;
          color: rgb(0, 0, 0) !important;
          cursor: pointer !important;
          z-index: 2147483647 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 12px !important;
          border: medium !important;
          box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 8px !important;
          min-width: 90px !important;
        }
 
        h3[class*="teaserHeading"] {
          all: unset !important;
          color: rgb(0, 0, 0) !important;
          font-size: 14px !important;
          font-weight: 300 !important;
          font-family: "Noto Sans", sans-serif !important;
          text-transform: uppercase !important;
        }
 
        span[class*="formCloseButton"] {
          all: unset !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 14px !important;
          height: 14px !important;
          border-radius: 50% !important;
          border: 1px solid rgb(0, 0, 0) !important;
          background-color: #fff !important;
          cursor: pointer !important;
        }
 
        span[class*="formCloseButton"] svg {
          width: 6px !important;
          height: 6px !important;
        }
 
        span[class*="formCloseButton"]:hover {
          color: red !important;
        }
 
        @media screen and (max-width: 768px) {
          button[class*="teaserContainer"] {
            font-size: 14px !important;
            padding-top: 54px !important;
            min-width: 100px !important;
            z-index: 6900 !important;
          }
 
          span[class*="formCloseButton"] svg {
            width: 10px !important;
            height: 10px !important;
          }
 
          span[class*="formCloseButton"] {
            all: unset !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 18px !important;
            height: 18px !important;
            border-radius: 50% !important;
            border: 1px solid rgb(0, 0, 0) !important;
            background-color: #fff !important;
            cursor: pointer !important;
          }
        }
      `);
 
      shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, styleSheet];
      scheduleShopifyFormsTeaserSync();
    } else if (document.querySelector('[data-testid="app-router"]')) {
      // App renders in light DOM (no shadow). Scope to Shopify Forms hosts only —
      // unscoped rules were leaking onto Klaviyo flyouts (shared textBody/heading class patterns).
      const styleId = 'form-embed-inline-styles';
      if (!document.getElementById(styleId)) {
        const formsScope = ':is(shopify-forms-embed, form-embed, #app-embed, [data-testid="app-router"])';
        const styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
          ${formsScope} button._teaserContainer_trejf_5{all:unset!important;position:fixed!important;top:50%!important;left:0!important;transform:translateY(-74%)!important;transform-origin:top left!important;background:#F9F7F4!important;padding:12px 14px!important;font-size:14px!important;line-height:100%!important;font-family:"Noto Sans",sans-serif!important;font-weight: 300!important;text-transform:uppercase!important;color:#000!important;cursor:pointer!important;z-index:2147483647!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;gap:12px!important;border:medium!important;box-shadow:rgba(0,0,0,.1) 0 2px 8px!important;min-width:90px!important}
          @media(max-width:767.98px){body:not(.template-product) ${formsScope} button._teaserContainer_trejf_5,body:not(.template-product) ${formsScope} button[class*="teaserContainer"]{z-index:6900!important}}
          ${formsScope} h3._teaserHeading_trejf_28{all:unset!important;color:#000!important;font-size:14px!important;font-weight: 300!important;font-family:"Noto Sans",sans-serif!important;text-transform:uppercase!important}
          ${formsScope} h2._textHeading_2aowh_35{font-family:"Noto Sans",sans-serif!important;font-weight: 300!important;font-size:24px!important}
          ${formsScope} ._textBody_2aowh_10,${formsScope} span._textBody_2aowh_10,${formsScope} p._textBody_2aowh_10,${formsScope} input._formInputField_237zm_7,${formsScope} input._formInputField_237zm_7::placeholder,${formsScope} button._formSubmitButton_8skcf_90,${formsScope} div._formDisclaimer_8skcf_38{font-family:"Noto Sans",sans-serif!important;font-weight:300!important}
          ${formsScope} button._formSubmitButton_8skcf_90,${formsScope} button._formSubmitButton_8skcf_90 *,${formsScope} button[class*="formSubmitButton"],${formsScope} button[class*="formSubmitButton"] *{color:#fff!important;-webkit-text-fill-color:#fff!important}
          ${formsScope} label[class*="radio"],${formsScope} [class*="radio"] label,${formsScope} [class*="choice"] label,${formsScope} [class*="choiceLabel"],${formsScope} [class*="radio"] span[class*="textBody"],${formsScope} [class*="choice"] span[class*="textBody"]{font-size:14px!important;font-family:"Noto Sans",sans-serif!important;font-weight:300!important}
          ${formsScope} span._formCloseButton_1684x_4{all:unset!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;width:14px!important;height:14px!important;border-radius:50%!important;border:1px solid #000!important;background:#fff!important;cursor:pointer!important}
          ${formsScope} span._formCloseButton_1684x_4 svg{width:6px!important;height:6px!important}
          @media(max-width:768px){
            ${formsScope} button._teaserContainer_trejf_5{font-size:14px!important;padding-top:54px!important;min-width:100px!important}
            ${formsScope} span._formCloseButton_1684x_4{width:18px!important;height:18px!important}
            ${formsScope} span._formCloseButton_1684x_4 svg{width:10px!important;height:10px!important}
          }
        `;
        document.head.appendChild(styleEl);
      }
    }
    // New: app embed root in light DOM
    const appEmbed = document.getElementById('app-embed');
    if (appEmbed) {
      var stackLowNow =
        window.matchMedia(MOBILE_FORMS_TEASER_MQ).matches &&
        !document.body.classList.contains('template-product') &&
        !isShopifyFormsModalOpen();
      try {
        Object.assign(appEmbed.style, {
          position: 'fixed',
          zIndex: stackLowNow ? FORMS_TEASER_Z_UNDER_OVERLAYS : FORMS_TEASER_Z_ABOVE_CHROME,
          left: '0px',
          right: 'unset',
          bottom: 'unset',
          top: '50%',
          transform: 'translateY(-74%)',
          rotate: '0deg',
          padding: '',
          width: ''
        });
      } catch (e) {}

      const styleId2 = 'form-embed-inline-styles-app-embed';
      if (!document.getElementById(styleId2)) {
        const styleEl2 = document.createElement('style');
        styleEl2.id = styleId2;
        styleEl2.textContent = `
          #app-embed button._teaserContainer_trejf_5{all:unset!important;position:fixed!important;top:50%!important;left:0!important;transform:translateY(-74%)!important;transform-origin:top left!important;background:#F9F7F4!important;padding:12px 14px!important;font-size:14px!important;line-height:100%!important;font-family:"Noto Sans",sans-serif!important;font-weight: 300!important;text-transform:uppercase!important;color:#000!important;cursor:pointer!important;z-index:2147483647!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;gap:12px!important;box-shadow:rgba(0,0,0,.1) 0 2px 8px!important;min-width:90px!important}
          @media(max-width:767.98px){body:not(.template-product) #app-embed{z-index:6900!important}body:not(.template-product) #app-embed button._teaserContainer_trejf_5{z-index:6900!important}}
          #app-embed h3._teaserHeading_trejf_28{all:unset!important;color:#000!important;font-size:14px!important;font-weight: 300!important;font-family:"Noto Sans",sans-serif!important;text-transform:uppercase!important}
          #app-embed h2._textHeading_2aowh_35{font-family:"Noto Sans",sans-serif!important;font-weight: 300!important;font-size:24px!important}
          #app-embed ._textBody_2aowh_10,#app-embed span._textBody_2aowh_10,#app-embed p._textBody_2aowh_10,#app-embed input._formInputField_237zm_7,#app-embed input._formInputField_237zm_7::placeholder,#app-embed button._formSubmitButton_8skcf_90,#app-embed div._formDisclaimer_8skcf_38{font-family:"Noto Sans",sans-serif!important;font-weight:300!important}
          #app-embed button._formSubmitButton_8skcf_90,#app-embed button._formSubmitButton_8skcf_90 *,#app-embed button[class*="formSubmitButton"],#app-embed button[class*="formSubmitButton"] *{color:#fff!important;-webkit-text-fill-color:#fff!important}
          #app-embed label[class*="radio"],#app-embed [class*="radio"] label,#app-embed [class*="choice"] label,#app-embed [class*="choiceLabel"],#app-embed [class*="radio"] span[class*="textBody"],#app-embed [class*="choice"] span[class*="textBody"]{font-size:14px!important;font-family:"Noto Sans",sans-serif!important;font-weight:300!important}
          #app-embed span._formCloseButton_1684x_4{all:unset!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;width:14px!important;height:14px!important;border-radius:50%!important;border:1px solid #000!important;background:#fff!important;cursor:pointer!important}
          #app-embed span._formCloseButton_1684x_4 svg{width:6px!important;height:6px!important}
          @media(max-width:768px){
            #app-embed button._teaserContainer_trejf_5{font-size:14px!important;padding-top:54px!important;min-width:100px!important;z-index:6900!important}
            #app-embed span._formCloseButton_1684x_4{width:18px!important;height:18px!important}
            #app-embed span._formCloseButton_1684x_4 svg{width:10px!important;height:10px!important}
          }
        `;
        document.head.appendChild(styleEl2);
      }
 
      // Force inline styles in case app CSS wins in specificity
      const teaserBtn2 = appEmbed.querySelector(
        'button[class*="teaserContainer"], button[class*="TeaserContainer"], button._teaserContainer_trejf_5'
      );
      if (teaserBtn2) {
        Object.assign(teaserBtn2.style, {
          position: 'fixed',
          top: '50%',
          left: '0px',
          transform: 'translateY(-74%)',
          backgroundColor: '#F9F7F4',
          padding: '12px 14px',
          fontSize: '14px',
          lineHeight: '100%',
          fontFamily: 'Noto Sans, sans-serif',
          fontWeight: '300',
          textTransform: 'uppercase',
          color: '#000',
          cursor: 'pointer',
          zIndex: stackLowNow ? FORMS_TEASER_Z_UNDER_OVERLAYS : FORMS_TEASER_Z_ABOVE_CHROME,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          boxShadow: 'rgba(0,0,0,0.1) 0px 2px 8px',
          minWidth: '90px'
        });
        const heading2 = teaserBtn2.querySelector('h3');
        if (heading2) {
          Object.assign(heading2.style, {
            color: '#000',
            fontSize: '14px',
            fontWeight: '300',
            fontFamily: 'Noto Sans, sans-serif',
            textTransform: 'uppercase'
          });
        }
        const close2 = teaserBtn2.querySelector('span');
        if (close2) {
          Object.assign(close2.style, {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            border: '1px solid #000',
            background: '#fff',
            cursor: 'pointer'
          });
          const svg2 = close2.querySelector('svg');
          if (svg2) { svg2.style.width = '6px'; svg2.style.height = '6px'; }
        }
      }
    }
    scheduleShopifyFormsTeaserSync();
  });
 
  observer.observe(document.body, { childList: true, subtree: true });
});
 
 
 
 
 
 
 
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.custom-store-gallery').forEach(function (gallerySection) {
      const mainSlider = gallerySection.querySelector('.main-image-slider');
      const slides = gallerySection.querySelectorAll('.main-slide');
      const thumbnails = gallerySection.querySelectorAll('.thumbnail-image');
      const track = gallerySection.querySelector('.thumbnails-track');
      const leftArrow = gallerySection.querySelector('.thumb-arrow.left');
      const rightArrow = gallerySection.querySelector('.thumb-arrow.right');
      const mainLeftArrow = gallerySection.querySelector('.main-arrow.left');
      const mainRightArrow = gallerySection.querySelector('.main-arrow.right');
 
      let currentIndex = 0;
 
      function goToSlide(index) {
        mainSlider.style.transform = `translateX(-${index * 100}%)`;
        thumbnails.forEach(t => t.classList.remove('active-thumb'));
        if (thumbnails[index]) {
          thumbnails[index].classList.add('active-thumb');
         
        }
        currentIndex = index;
      }
 
      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => goToSlide(index));
      });
   const scroller = gallerySection.querySelector('.thumbnails-track-wrapper');
    if (leftArrow && rightArrow && scroller) {
      leftArrow.addEventListener('click', () => {
        scroller.scrollBy({ left: -scroller.clientWidth, behavior: 'smooth' });
      });
      rightArrow.addEventListener('click', () => {
        scroller.scrollBy({ left: scroller.clientWidth, behavior: 'smooth' });
      });
    }
 
      if (mainLeftArrow && mainRightArrow) {
        mainLeftArrow.addEventListener('click', () => {
          const newIndex = (currentIndex - 1 + slides.length) % slides.length;
          goToSlide(newIndex);
        });
 
        mainRightArrow.addEventListener('click', () => {
          const newIndex = (currentIndex + 1) % slides.length;
          goToSlide(newIndex);
        });
      }
 
      // Swipe support for mobile
      let startX = 0;
      mainSlider.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      });
 
      mainSlider.addEventListener('touchend', function (e) {
        const endX = e.changedTouches[0].clientX;
        const diff = endX - startX;
 
        if (diff > 50) {
          const newIndex = (currentIndex - 1 + slides.length) % slides.length;
          goToSlide(newIndex);
        } else if (diff < -50) {
          const newIndex = (currentIndex + 1) % slides.length;
          goToSlide(newIndex);
        }
      });
 
      goToSlide(0);
    });
  });
 
 
 
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('form[data-newsletter-form]').forEach(function (form) {
    const cb  = form.querySelector('[id^="newsletter-terms-"]');
    const btn = form.querySelector('button[type="submit"]');
    if (!cb || !btn) return;
    const sync = () => { btn.disabled = !cb.checked; };
    sync();
    cb.addEventListener('change', sync);
  });
});
 
 
 
  if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }
 
      get selectedOptionValues() {
        // Collect selected values per option group, prioritizing radios over selects.
        // This avoids hidden mobile selects overriding visible desktop radios.
        const optionGroups = Array.from(this.querySelectorAll('.product-form__input'));
        return optionGroups
          .map((group) => {
            const checkedRadio = group.querySelector('input[type="radio"]:checked');
            if (checkedRadio && checkedRadio.dataset && checkedRadio.dataset.optionValueId) {
              return checkedRadio.dataset.optionValueId;
            }
            const selectedOption = group.querySelector('select option[selected]');
            return selectedOption ? selectedOption.dataset.optionValueId : undefined;
          })
          .filter((id) => typeof id !== 'undefined');
      }
 
      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }
 
      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }
 
      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;
 
        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');
 
          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }
 
          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
 
 
// Initialize variant scopes that are injected dynamically (e.g., quick add drawer)
document.addEventListener('DOMContentLoaded', function(){
  var bindAll = function () {
    if (window.__initVariantScope) {
      document.querySelectorAll('[data-variant-scope]').forEach(function(el){
        var sel = '[data-variant-scope="' + el.getAttribute('data-variant-scope') + '"]';
        window.__initVariantScope(sel);
      });
    }
  };
  bindAll();
  var mo = new MutationObserver(function(){ bindAll(); });
  mo.observe(document.body, { childList: true, subtree: true });
});
 
 
 
  // Wishlist refresh functionality
  document.addEventListener("DOMContentLoaded", function() {
    // Check if we just refreshed from wishlist (using sessionStorage)
    if (sessionStorage.getItem('wishlistToCart') === 'true') {
      // Clear the flag
      sessionStorage.removeItem('wishlistToCart');
     
      // Open cart drawer after a short delay
      setTimeout(function() {
        const cartButton = document.querySelector('[data-cart-toggle]') ||
                          document.querySelector('.cart-toggle') ||
                          document.querySelector('[data-drawer-trigger="cart"]');
        if (cartButton) {
          cartButton.click();
        }
      }, 100);
    }
 
    // Listen for clicks on "Move to Cart" buttons from wishlist app
    document.addEventListener('click', function(event) {
      // Check if the clicked element or its parent contains "Move to Cart" text
      const target = event.target;
      const text = target.textContent || target.innerText || '';
     
      // Only trigger on specific wishlist "Move to Cart" text
      if ((text.toLowerCase().includes('move to cart') ||
           text.toLowerCase().includes('add to cart')) &&
          (window.location.pathname.includes('/apps/wishlist') ||
           window.location.pathname.includes('/wishlist'))) {
       
        // Prevent the default action first
        event.preventDefault();
        event.stopPropagation();
       
        // Check if the item is available before proceeding
        const productElement = target.closest('[data-product-id]') ||
                              target.closest('.product-item') ||
                              target.closest('[class*="product"]');
       
        if (productElement) {
          // Try to get variant availability info
          const variantId = productElement.getAttribute('data-variant-id') ||
                           productElement.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');
         
          // If we can't determine availability, show a warning
          if (!variantId) {
            alert('Please check product availability before adding to cart. Some sizes may be out of stock.');
            return;
          }
        }
       
        // Set flag before refresh
        sessionStorage.setItem('wishlistToCart', 'true');
       
        // Add a longer delay to allow the wishlist app to process the action
        // Increased delay to prevent "too many attempts" errors
        setTimeout(function() {
          window.location.reload();
        }, 2000);
      }
    });
   
    // Handle wishlist variant availability
    if (window.location.pathname.includes('/apps/wishlist') ||
        window.location.pathname.includes('/wishlist')) {
     
      // Function to hide out-of-stock options
      function hideOutOfStockOptions() {
        const variantPickers = document.querySelectorAll('.wishlist-variant-picker');
       
        variantPickers.forEach(picker => {
          const options = picker.querySelectorAll('option');
          let hasAvailableOptions = false;
         
          options.forEach(option => {
            const inventoryQuantity = parseInt(option.getAttribute('data-inventory_quantity') || '0');
            const inventoryPolicy = option.getAttribute('data-inventory_policy');
           
            // Hide options that are out of stock
            if (inventoryQuantity === 0 && inventoryPolicy === 'DENY') {
              option.style.display = 'none';
              option.disabled = true;
            } else {
              hasAvailableOptions = true;
            }
          });
         
          // If no available options, show a message
          if (!hasAvailableOptions) {
            const noStockMessage = document.createElement('option');
            noStockMessage.text = 'Out of Stock';
            noStockMessage.disabled = true;
            noStockMessage.style.color = '#999';
            picker.appendChild(noStockMessage);
          }
        });
      }
     
      // Run immediately
      hideOutOfStockOptions();
     
      // Also run when new content is loaded (for dynamic wishlist updates)
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            hideOutOfStockOptions();
          }
        });
      });
     
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  });
 
 
  // Cart discount code functionality
  function initCartDiscount() {
    const discountInput = document.querySelector('[data-discount-input]');
    const discountButton = document.querySelector('[data-apply-discount]');
    const discountErrorMessage = document.querySelector('[data-discount-error-message]');
    const appliedContainer = document.querySelector('[data-applied-discounts]');

    if (!discountInput || !discountButton) return;

    let existingDiscountCodes = [];

    // Seed from current cart state so duplicates are caught and applied codes are shown
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.discount_codes && cart.discount_codes.length) {
          var applicable = cart.discount_codes.filter(function (d) { return d.applicable; });
          existingDiscountCodes = applicable.map(function (d) { return d.code; });
          renderAppliedCodes(applicable);
        }
      })
      .catch(function () {});

    function formatDiscountAmount(cents) {
      var format = (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) || '€{{amount}}';
      var amount = (cents / 100).toFixed(2);
      var amountWithComma = amount.replace('.', ',');
      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100));
    }

    function renderAppliedCodes(codes) {
      if (!appliedContainer || !codes.length) return;
      appliedContainer.innerHTML = codes.map(function (d) {
        var amountLabel = d.amount ? ' -' + formatDiscountAmount(Number(d.amount)) : '';
        return '<span class="cart-discount-code__tag">' +
          '<span class="cart-discount-code__tag-code">' + d.code + amountLabel + '</span>' +
          '<button type="button" class="cart-discount-code__tag-remove" data-remove-discount aria-label="Remove discount ' + d.code + '">&times;</button>' +
          '</span>';
      }).join('');

      appliedContainer.querySelectorAll('[data-remove-discount]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          removeDiscount();
        });
      });
    }

    function applyDiscount(discountCode) {
      if (existingDiscountCodes.includes(discountCode)) {
        showDiscountMessage('Discount already applied', 'error');
        return;
      }
      existingDiscountCodes.push(discountCode);
      updateCartDiscounts(existingDiscountCodes.join(','));
    }

    function removeDiscount() {
      existingDiscountCodes = [];
      updateCartDiscounts('');
    }

    function updateCartDiscounts(discountString) {
      var lastAttemptedDiscount = discountString
        .split(',')
        .filter(function (c) { return c; })
        .pop();
      if (lastAttemptedDiscount) lastAttemptedDiscount = lastAttemptedDiscount.trim();

      showDiscountMessage('Applying discount...', 'loading');

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: discountString }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP error ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (lastAttemptedDiscount) {
            var wasApplied = data.discount_codes && Array.isArray(data.discount_codes) &&
              data.discount_codes.some(function (d) { return d.code === lastAttemptedDiscount && d.applicable; });

            if (!wasApplied) {
              existingDiscountCodes = existingDiscountCodes.filter(function (c) { return c !== lastAttemptedDiscount; });
              showDiscountMessage('Discount not applicable', 'error');
            } else {
              showDiscountMessage('Discount applied successfully!', 'success');
              setTimeout(function () { window.location.reload(); }, 1000);
            }
          } else {
            showDiscountMessage('Discount removed', 'success');
            setTimeout(function () { window.location.reload(); }, 1000);
          }
        })
        .catch(function (error) {
          console.error('Error updating cart discounts:', error);
          showDiscountMessage('Error applying discount code', 'error');
        });
    }

    function showDiscountMessage(text, type) {
      if (!discountErrorMessage) return;
      discountErrorMessage.textContent = text;
      discountErrorMessage.className = 'cart-discount-code__message cart-discount-code__message--' + type;
      if (type !== 'loading') {
        setTimeout(function () { discountErrorMessage.classList.add('hidden'); }, 3000);
      }
    }

    discountButton.addEventListener('click', function (event) {
      event.preventDefault();
      var code = discountInput.value.trim();
      discountInput.value = '';
      if (code) applyDiscount(code);
    });

    discountInput.addEventListener('keypress', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        discountButton.click();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initCartDiscount();
  });
 
  // Wishlist Vendor Display Functionality - Get Real Vendor from Product API
  (function() {
    'use strict';
   
    // Function to add vendor to wishlist item
    function addVendorToItem(wishlistItem, vendor) {
      const titleElement = wishlistItem.querySelector('.wihslist-meta h3');
      if (titleElement && vendor) {
        titleElement.setAttribute('data-vendor', vendor);
      }
    }
   
    // Function to fetch vendor from Shopify product API using handle
    async function fetchVendorFromAPI(productUrl) {
      try {
        // Extract product handle from URL
        const urlParts = productUrl.split('/');
        const productHandle = urlParts[urlParts.length - 1].split('?')[0];
       
        const response = await fetch(`/products/${productHandle}.js`);
       
        if (response.ok) {
          const product = await response.json();
          return product.vendor;
        }
      } catch (error) {
        // Silent fail - no vendor found
      }
      return null;
    }
   
    // Function to process all wishlist items
    async function processWishlistItems() {
      const wishlistItems = document.querySelectorAll('.wishlist-page .wishlist-column');
     
      for (const item of wishlistItems) {
        const titleElement = item.querySelector('.wihslist-meta h3 a');
        const productUrl = titleElement?.getAttribute('href');
       
        if (productUrl) {
          const vendor = await fetchVendorFromAPI(productUrl);
          if (vendor) {
            addVendorToItem(item, vendor);
          }
        }
      }
    }
   
    // Function to initialize the script
    function init() {
      // Run immediately
      processWishlistItems();
     
      // Also run when wishlist content changes (for dynamic loading)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            processWishlistItems();
          }
        });
      });
     
      // Start observing
      const wishlistPage = document.querySelector('.wishlist-page');
      if (wishlistPage) {
        observer.observe(wishlistPage, {
          childList: true,
          subtree: true
        });
      }
    }
   
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
 
 
   // Mobile Slider Dots for Section Columns
  (function() {
    'use strict';
   
    function initSliderDots() {
      const mobileSliders = document.querySelectorAll('.section-columns .grid--mobile-slider');
     
      mobileSliders.forEach(slider => {
        const gridItems = slider.querySelectorAll('.grid-item');
        const totalSlides = gridItems.length;
       
        if (totalSlides <= 1) return; // No need for dots if only one slide
       
        // Create dots
        createSliderDots(slider, totalSlides);
       
        // Initialize dot tracking
        initDotTracking(slider, totalSlides);
      });
    }
   
    function createSliderDots(slider, totalSlides) {
      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'slider-dots';
     
      for (let i = 0; i < totalSlides; i++) {
        const dot = document.createElement('div');
        dot.className = 'slider-dot';
        if (i === 0) dot.classList.add('active');
        dotsContainer.appendChild(dot);
      }
     
      // Add dots to the section (parent of slider)
      const section = slider.closest('.section-columns');
      if (section) {
        section.appendChild(dotsContainer);
      }
    }
   
    function initDotTracking(slider, totalSlides) {
      const dots = slider.closest('.section-columns').querySelectorAll('.slider-dot');
      const gridItems = slider.querySelectorAll('.grid-item');
     
      function updateDots() {
        const slideWidth = gridItems[0].offsetWidth;
        const currentSlide = Math.round(slider.scrollLeft / slideWidth);
       
        dots.forEach((dot, index) => {
          if (index === currentSlide) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
     
      // Update dots on scroll
      slider.addEventListener('scroll', updateDots);
     
      // Initial update
      updateDots();
    }
   
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSliderDots);
    } else {
      initSliderDots();
    }
  })();

  // PDP (main product): Add to Bag + Notify Me (Restock Alerts) hover tooltip when Color/Size not chosen yet
  (function () {
    'use strict';

    var COLOR_NAMES = { color: 1, colour: 1, 'χρώμα': 1 };
    var SIZE_NAMES = { size: 1, 'μέγεθος': 1 };
    var notifyTooltipSeq = 0;

    function norm(s) {
      return String(s || '').trim().toLowerCase();
    }

    function getMissingOptions(picker) {
      var missing = [];
      picker.querySelectorAll('.option-selector:not(.option-selector--custom)').forEach(function (el) {
        if (el.dataset.selectorType === 'dropdown') {
          var selected = el.querySelector('.custom-select__option[aria-selected="true"]');
          if (!selected || selected.dataset.value === '') {
            if (el.dataset.option) missing.push(el.dataset.option);
          }
        } else {
          var checked = el.querySelector('input.js-option:checked');
          if (!checked && el.dataset.option) missing.push(el.dataset.option);
        }
      });
      return missing;
    }

    function tooltipText(missing, strings) {
      var cats = { color: false, size: false, other: [] };
      missing.forEach(function (name) {
        var k = norm(name);
        if (COLOR_NAMES[k]) cats.color = true;
        else if (SIZE_NAMES[k]) cats.size = true;
        else cats.other.push(name);
      });

      if (cats.other.length) {
        var opts = missing
          .map(function (n) {
            return String(n).toLowerCase();
          })
          .join(', ');
        return strings.fallback.replace('[options]', opts);
      }
      if (cats.color && cats.size) return strings.both;
      if (cats.color) return strings.color;
      if (cats.size) return strings.size;
      return strings.both;
    }

    function isIncompleteVariantSelection(picker) {
      if (!picker || !picker.classList.contains('variant-picker--preselection')) return false;
      return getMissingOptions(picker).length > 0;
    }

    var KLAVIYO_BIS_TRIGGER_SELECTOR =
      'a.klaviyo-bis-trigger, button.klaviyo-bis-trigger, .klaviyo-bis-anchor .klaviyo-bis-trigger, .klaviyo-bis-anchor button';

    /** Block Klaviyo BIS popup until size/color match Add to bag rules; reuse ProductForm.validate() UI. */
    function bindNotifyClickGuard(productFormEl) {
      if (productFormEl.dataset.notifyClickGuardBound) return;
      productFormEl.dataset.notifyClickGuardBound = '1';

      function hasSelectedVariantId() {
        var idInput = productFormEl.querySelector('input[name="id"]');
        return !!(idInput && String(idInput.value || '').trim());
      }

      function guardNotifyInteraction(evt) {
        var btn =
          evt.target && evt.target.closest && evt.target.closest(KLAVIYO_BIS_TRIGGER_SELECTOR);
        if (!btn || !productFormEl.contains(btn)) return;
        // A selected variant (including OOS) must never swallow Klaviyo's document-level click.
        if (hasSelectedVariantId()) return;
        var picker = productFormEl.querySelector('variant-picker');
        if (!isIncompleteVariantSelection(picker)) return;
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        if (typeof productFormEl.validate === 'function') {
          productFormEl.validate();
        }
      }

      productFormEl.addEventListener('click', guardNotifyInteraction, true);
      productFormEl.addEventListener('keydown', function (evt) {
        if (evt.key !== 'Enter' && evt.key !== ' ') return;
        guardNotifyInteraction(evt);
      }, true);
    }

    function bindVariantHoverWrap(wrap, opts) {
      var boundKey = opts.boundKey;
      if (wrap.dataset[boundKey]) return;
      wrap.dataset[boundKey] = '1';

      var tooltip = wrap.querySelector('.add-to-cart-tooltip');
      var textEl = wrap.querySelector('.add-to-cart-tooltip__text');
      var btn = wrap.querySelector(opts.buttonSelector);
      if (!tooltip || !textEl || !btn) return;

      var rootForm = wrap.closest('product-form');
      var picker = rootForm ? rootForm.querySelector('variant-picker') : null;

      function hide() {
        wrap.classList.remove(opts.openClass);
        tooltip.setAttribute('aria-hidden', 'true');
        btn.removeAttribute('aria-describedby');
      }

      function showIfEligible() {
        var strings = window.theme && theme.strings ? theme.strings : {};
        if (
          !picker ||
          !strings.atcTooltipSize ||
          !strings.atcTooltipColor ||
          !strings.atcTooltipBoth ||
          !strings.atcTooltipFallback
        ) {
          hide();
          return;
        }
        if (!picker.classList.contains('variant-picker--preselection')) {
          hide();
          return;
        }
        var missing = getMissingOptions(picker);
        if (!missing.length) {
          hide();
          return;
        }
        var msg = tooltipText(missing, {
          size: strings.atcTooltipSize,
          color: strings.atcTooltipColor,
          both: strings.atcTooltipBoth,
          fallback: strings.atcTooltipFallback
        });
        textEl.textContent = msg;
        btn.setAttribute('aria-describedby', tooltip.id);
        tooltip.setAttribute('aria-hidden', 'false');
        wrap.classList.add(opts.openClass);
      }

      wrap.addEventListener('mouseenter', showIfEligible);
      wrap.addEventListener('mouseleave', hide);
    }

    function bindWrap(wrap) {
      bindVariantHoverWrap(wrap, {
        buttonSelector: '.add-to-cart[name="add"]',
        openClass: 'add-to-cart-hover-wrap--tooltip-open',
        boundKey: 'atcTooltipBound'
      });
    }

    function bindNotifyWrap(wrap) {
      bindVariantHoverWrap(wrap, {
        buttonSelector: KLAVIYO_BIS_TRIGGER_SELECTOR,
        openClass: 'notify-me-hover-wrap--tooltip-open',
        boundKey: 'notifyTooltipBound'
      });
    }

    function ensureNotifyHoverWraps(scope) {
      scope.querySelectorAll('product-form.quickbuy-content ' + KLAVIYO_BIS_TRIGGER_SELECTOR).forEach(function (btn) {
        if (btn.closest('.notify-me-hover-wrap')) return;
        // Main PDP <product-form> also has quickbuy-content. Never relocate Klaviyo's live trigger.
        if (btn.closest('.klaviyo-bis-anchor')) return;

        var wrap = document.createElement('div');
        wrap.className = 'notify-me-hover-wrap';
        var tooltipId = 'NotifyMeTooltip-' + (++notifyTooltipSeq);

        var tooltip = document.createElement('div');
        tooltip.className = 'add-to-cart-tooltip';
        tooltip.id = tooltipId;
        tooltip.setAttribute('role', 'tooltip');
        tooltip.setAttribute('aria-hidden', 'true');

        var inner = document.createElement('div');
        inner.className = 'add-to-cart-tooltip__inner';
        var textSpan = document.createElement('span');
        textSpan.className = 'add-to-cart-tooltip__text';
        inner.appendChild(textSpan);
        tooltip.appendChild(inner);
        wrap.appendChild(tooltip);

        btn.parentNode.insertBefore(wrap, btn);
        wrap.appendChild(btn);
      });
    }

    function observeNotifyInjection() {
      document.querySelectorAll('product-form.quickbuy-content').forEach(function (form) {
        if (form.dataset.notifyTooltipObserver) return;
        form.dataset.notifyTooltipObserver = '1';
        var t = null;
        var obs = new MutationObserver(function () {
          if (t) clearTimeout(t);
          t = setTimeout(function () {
            ensureNotifyHoverWraps(form);
            form.querySelectorAll('.notify-me-hover-wrap').forEach(bindNotifyWrap);
          }, 80);
        });
        obs.observe(form, { childList: true, subtree: true });
      });
    }

    function init(root) {
      var scope = root && root.querySelector ? root : document;
      ensureNotifyHoverWraps(scope);
      scope.querySelectorAll('product-form.quickbuy-content').forEach(bindNotifyClickGuard);
      scope.querySelectorAll('product-form.quickbuy-content .add-to-cart-hover-wrap').forEach(bindWrap);
      scope.querySelectorAll('product-form.quickbuy-content .notify-me-hover-wrap').forEach(bindNotifyWrap);
      observeNotifyInjection();
    }

    /** Re-run init after consent / late Klaviyo onsite embeds. */
    function scheduleNotifyResilience() {
      [500, 1500, 3500].forEach(function (ms) {
        setTimeout(function () {
          init(document);
        }, ms);
      });
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(
          function () {
            init(document);
          },
          { timeout: 4000 }
        );
      }
      window.addEventListener('load', function onLoad() {
        window.removeEventListener('load', onLoad);
        init(document);
      });
      document.addEventListener('visitorConsentCollected', function () {
        init(document);
      });
      window.addEventListener('pageshow', function (ev) {
        if (ev.persisted) init(document);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        init();
        scheduleNotifyResilience();
      });
    } else {
      init();
      scheduleNotifyResilience();
    }

    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector) init(e.target);
    });

    document.addEventListener('on:quickbuy:after-open', function (e) {
      if (e.target && e.target.querySelector) init(e.target);
    });

    document.addEventListener('on:variant:change', function () {
      setTimeout(function () {
        init(document);
      }, 50);
    });
  })();

  /* ===========================================================================
     Klaviyo BIS trigger — theme locale label + Add to Bag styling (Klaviyo form is single-language).
     ========================================================================= */
  (function () {
    'use strict';

    var BIS_ANCHOR_SEL = '.klaviyo-bis-anchor';
    var BIS_BTN_SEL =
      '.klaviyo-bis-anchor .klaviyo-bis-trigger, .klaviyo-bis-anchor button, .klaviyo-bis-anchor a.klaviyo-bis-trigger';

    function getBisLabel() {
      return (window.theme && theme.strings && theme.strings.notifyWhenAvailable) || 'Notify me when available';
    }

    function syncKlaviyoBisButton(scope) {
      var root = scope && scope.querySelector ? scope : document;
      var label = getBisLabel();
      root.querySelectorAll(BIS_BTN_SEL).forEach(function (btn) {
        btn.classList.add('btn', 'btn--brand', 'btn--large', 'klaviyo-bis-trigger');
        if (!btn.hasAttribute('klaviyo-bis-trigger')) {
          btn.setAttribute('klaviyo-bis-trigger', '');
        }
        // Never wipe Klaviyo-injected children — textContent would destroy their click binding.
        if (!btn.children.length && btn.textContent.trim() !== label) {
          btn.textContent = label;
        }
        if (!btn.getAttribute('aria-label')) {
          btn.setAttribute('aria-label', label);
        }
        if (btn.tagName === 'A') {
          btn.setAttribute('role', 'button');
          if (!btn.getAttribute('href')) btn.setAttribute('href', '#');
        } else if (btn.tagName === 'BUTTON' && !btn.getAttribute('type')) {
          btn.setAttribute('type', 'button');
        }
      });
    }

    function observeBisAnchors(scope) {
      var root = scope && scope.querySelector ? scope : document;
      root.querySelectorAll(BIS_ANCHOR_SEL).forEach(function (anchor) {
        if (anchor.dataset.aesthetBisObserved === '1') return;
        anchor.dataset.aesthetBisObserved = '1';
        var syncTimer = null;
        var mo = new MutationObserver(function () {
          if (syncTimer) clearTimeout(syncTimer);
          syncTimer = setTimeout(function () {
            syncKlaviyoBisButton(anchor);
          }, 50);
        });
        mo.observe(anchor, { childList: true, subtree: true });
      });
    }

    function tryReinitKlaviyoBis() {
      try {
        if (window._klaviyo && typeof window._klaviyo.init === 'function') {
          window._klaviyo.init();
        }
      } catch (err) { /* Klaviyo API is optional */ }
    }

    function bindBisAnchorClickSafety(scope) {
      var root = scope && scope.querySelector ? scope : document;
      root.querySelectorAll(BIS_ANCHOR_SEL).forEach(function (anchor) {
        if (anchor.dataset.aesthetBisClickBound === '1') return;
        anchor.dataset.aesthetBisClickBound = '1';
        anchor.addEventListener('click', function (evt) {
          var trigger = evt.target && evt.target.closest && evt.target.closest(BIS_BTN_SEL);
          if (!trigger || !anchor.contains(trigger)) return;
          if (trigger.tagName === 'A') {
            var href = trigger.getAttribute('href');
            if (!href || href === '#') evt.preventDefault();
          }
        });
      });
    }

    function initBisTrigger(root) {
      syncKlaviyoBisButton(root);
      observeBisAnchors(root);
      bindBisAnchorClickSafety(root);
    }

    function scheduleBisResync() {
      [0, 100, 500, 1500, 3500].forEach(function (ms) {
        setTimeout(function () {
          initBisTrigger(document);
        }, ms);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initBisTrigger();
        scheduleBisResync();
      });
    } else {
      initBisTrigger();
      scheduleBisResync();
    }

    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector) initBisTrigger(e.target);
    });

    document.addEventListener('on:variant:change', function () {
      setTimeout(function () {
        initBisTrigger(document);
        tryReinitKlaviyoBis();
      }, 50);
    });

    document.addEventListener('visitorConsentCollected', function () {
      scheduleBisResync();
    });
  })();

  /* ===========================================================================
     PDP — open Klaviyo back-in-stock when arriving with ?notify=1 (Get the Look, PLP).
     Klaviyo only binds BIS on the live product page; off-PDP clones cannot trigger it.
     ========================================================================= */
  (function () {
    'use strict';

    if (!document.body.classList.contains('template-product')) return;

    var params = new URLSearchParams(window.location.search);
    if (params.get('notify') !== '1') return;

    var opened = false;

    function findPdpBisTrigger() {
      var section = document.querySelector('.section-main-product');
      if (!section) return null;
      return section.querySelector(
        '.klaviyo-bis-anchor .klaviyo-bis-trigger, .klaviyo-bis-anchor button, .klaviyo-bis-anchor a.klaviyo-bis-trigger'
      );
    }

    function isVisible(el) {
      return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    }

    function cleanNotifyParam() {
      params.delete('notify');
      var query = params.toString();
      var nextUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState({}, '', nextUrl);
    }

    function tryOpenBis() {
      if (opened) return true;
      var btn = findPdpBisTrigger();
      if (!isVisible(btn)) return false;
      opened = true;
      btn.click();
      cleanNotifyParam();
      return true;
    }

    function scheduleOpenAttempts() {
      [0, 100, 300, 500, 1000, 1500, 2500, 4000, 6000].forEach(function (ms) {
        setTimeout(tryOpenBis, ms);
      });
    }

    if (!tryOpenBis()) {
      var observer = new MutationObserver(function () {
        if (tryOpenBis()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        observer.disconnect();
      }, 6500);
      scheduleOpenAttempts();
    }
  })();

  /**
   * Cart drawer + main cart page lines — Wishlist Plus (Swym) via storefront SDK (same lists as PDP/collections).
   * Not `.wishlist-engine`; guests rely on theme login modal intercept on `[data-cart-swym-wishlist]`.
   *
   * Click handling is delegated (one document listener). Per-button addEventListener +
   * on:cart:after-merge rebind used to stack handlers on preserved merge nodes, which made
   * Move/Remove race (optimistic label flip without cart remove, or remove-of-nothing).
   * Listed-state matches product+variant only — same as PDP/PLP (not “any variant of product”).
   */
  (function () {
    window.theme = window.theme || {};
    /* Hearts: drawer pairing rows + any cart-line heart. Move link: cart page + drawer lines. */
    var TOGGLE_SEL = '.cart-line-wishlist-btn[data-cart-swym-wishlist]';
    var MOVE_SEL = '[data-cart-move-to-wishlist]';
    var CLICK_SEL = TOGGLE_SEL + ', ' + MOVE_SEL;
    var swymSflSuppressUntil = 0;
    var swymSflSuppressObserver = null;

    function notifyWishlistToast(isRemoved) {
      if (typeof window.aesthetShowWishlistToast === 'function') {
        window.aesthetShowWishlistToast(!!isRemoved);
      }
    }

    /* Wishlist page hydrates from this key before fetchLists returns — clear on
       storefront mutations so a just-removed item cannot flash back from cache. */
    function invalidateWishlistPageCache() {
      try {
        sessionStorage.removeItem('swym-wishlist-products');
      } catch (e) {
        /* ignore */
      }
    }
    window.theme.invalidateWishlistPageCache = invalidateWishlistPageCache;

    function swymUnlockScroll() {
      document.body.classList.remove('swym-no-scroll', 'swym-modal-open');
      document.documentElement.classList.remove('swym-no-scroll', 'swym-modal-open');
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
    }

    function tearDownSwymSflModal() {
      var modal = document.querySelector('.swym-modal.swym-sfl-popup');
      if (!modal || !modal.isConnected) return;

      var box = modal.closest('.swym-modal-container');
      if (box && box.parentNode) {
        box.parentNode.removeChild(box);
      } else if (modal.parentNode) {
        modal.parentNode.removeChild(modal);
      }

      swymUnlockScroll();
    }

    function armSwymSflSuppression(durationMs) {
      swymSflSuppressUntil = Date.now() + (durationMs || 5000);
      tearDownSwymSflModal();

      if (swymSflSuppressObserver) return;

      swymSflSuppressObserver = new MutationObserver(function () {
        if (Date.now() > swymSflSuppressUntil) return;
        if (document.querySelector('.swym-modal.swym-sfl-popup')) {
          tearDownSwymSflModal();
        }
      });
      swymSflSuppressObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    window.theme.armSwymSflSuppression = armSwymSflSuppression;
    window.theme.tearDownSwymSflModal = tearDownSwymSflModal;

    function payloadFromBtn(btn) {
      return {
        empi: parseInt(btn.getAttribute('data-empi'), 10),
        epi: parseInt(btn.getAttribute('data-epi'), 10),
        du: btn.getAttribute('data-du') || '',
        dt: btn.getAttribute('data-dt') || '',
        iu: btn.getAttribute('data-iu') || '',
        pr: parseInt(btn.getAttribute('data-pr') || '0', 10) || 0,
        qty: 1,
      };
    }

    function itemIsListed(list, empi, epi) {
      if (!list || !list.length) return false;
      var empiStr = String(empi);
      var epiStr = String(epi);
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (!it) continue;
        var itEmpi = String(
          it.empi != null ? it.empi : it.product_id != null ? it.product_id : ''
        );
        var itEpi = String(
          it.epi != null ? it.epi : it.variant_id != null ? it.variant_id : ''
        );
        if (itEmpi === empiStr && itEpi === epiStr) return true;
      }
      return false;
    }

    function syncUi(btn, onList) {
      btn.classList.toggle('is-cart-wishlisted', onList);
      btn.setAttribute('aria-pressed', onList ? 'true' : 'false');
      var labelIn = btn.getAttribute('data-aria-in');
      var labelOut = btn.getAttribute('data-aria-out');
      if (labelIn && labelOut) {
        btn.setAttribute('aria-label', onList ? labelOut : labelIn);
      }
    }

    function syncMoveUi(btn, onList) {
      btn.classList.toggle('is-cart-wishlisted', onList);
      btn.setAttribute('aria-pressed', onList ? 'true' : 'false');
      var labelMove =
        btn.getAttribute('data-label-move') ||
        btn.getAttribute('data-label-add') ||
        'Move to Wishlist';
      var labelRemove =
        btn.getAttribute('data-label-remove') || 'Remove from wishlist';
      var label = onList ? labelRemove : labelMove;
      btn.textContent = label;
      btn.setAttribute('aria-label', label);
    }

    function setBusy(btn, busy) {
      btn.classList.toggle('is-busy', !!busy);
      if (busy) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    }

    function removeCartLineFromBtn(btn, options) {
      var skipSfl = options && options.skipSfl;
      if (skipSfl) armSwymSflSuppression();

      var cartItem = btn.closest('.cart-item');
      var cartForm = btn.closest('cart-form');
      if (cartForm && typeof cartForm.adjustItemQuantity === 'function' && cartItem) {
        cartForm.adjustItemQuantity(cartItem, { to: 0 });
        if (skipSfl) setTimeout(tearDownSwymSflModal, 0);
        return true;
      }
      return false;
    }

    function paintAllButtons(swat) {
      if (!swat || typeof swat.fetch !== 'function') return;
      swat.fetch(function (list) {
        var hearts = document.querySelectorAll(TOGGLE_SEL);
        for (var i = 0; i < hearts.length; i++) {
          var h = hearts[i];
          if (h.classList.contains('is-busy')) continue;
          var hp = payloadFromBtn(h);
          if (!hp.empi || !hp.epi) continue;
          syncUi(h, itemIsListed(list, hp.empi, hp.epi));
        }
        var moves = document.querySelectorAll(MOVE_SEL);
        for (var j = 0; j < moves.length; j++) {
          var m = moves[j];
          if (m.classList.contains('is-busy')) continue;
          var mp = payloadFromBtn(m);
          if (!mp.empi || !mp.epi) continue;
          syncMoveUi(m, itemIsListed(list, mp.empi, mp.epi));
        }
      });
    }

    function handleHeartClick(swat, button) {
      var p = payloadFromBtn(button);
      if (!p.empi || !p.epi) return;
      if (typeof swat.addToWishList !== 'function' || typeof swat.removeFromWishList !== 'function') {
        return;
      }

      setBusy(button, true);
      var busyTimer = setTimeout(function () {
        setBusy(button, false);
      }, 8000);

      var clearBusy = function () {
        clearTimeout(busyTimer);
        setBusy(button, false);
      };

      swat.fetch(function (list) {
        var on = itemIsListed(list, p.empi, p.epi);
        if (on) {
          syncUi(button, false);
          notifyWishlistToast(true);
          swat.removeFromWishList(
            { empi: p.empi, epi: p.epi, du: p.du },
            function () {
              invalidateWishlistPageCache();
              clearBusy();
            },
            function () {
              syncUi(button, true);
              clearBusy();
            }
          );
        } else {
          syncUi(button, true);
          notifyWishlistToast(false);
          swat.addToWishList(
            p,
            function () {
              invalidateWishlistPageCache();
              clearBusy();
            },
            function () {
              syncUi(button, false);
              clearBusy();
            }
          );
        }
      });
    }

    function handleMoveClick(swat, button) {
      var p = payloadFromBtn(button);
      if (!p.empi || !p.epi) return;
      if (typeof swat.addToWishList !== 'function') return;

      setBusy(button, true);
      var busyTimer = setTimeout(function () {
        setBusy(button, false);
      }, 8000);

      var clearBusy = function () {
        clearTimeout(busyTimer);
        setBusy(button, false);
      };

      swat.fetch(function (list) {
        var on = itemIsListed(list, p.empi, p.epi);
        if (on) {
          /* Already wishlisted — remove from wishlist only (keep cart line). */
          if (typeof swat.removeFromWishList !== 'function') {
            clearBusy();
            return;
          }
          syncMoveUi(button, false);
          notifyWishlistToast(true);
          swat.removeFromWishList(
            { empi: p.empi, epi: p.epi, du: p.du },
            function () {
              invalidateWishlistPageCache();
              clearBusy();
            },
            function () {
              syncMoveUi(button, true);
              clearBusy();
            }
          );
        } else {
          /*
           * Move: add first; only then drop the cart line. Do not flip the label to
           * “Remove from wishlist” before the line is gone — that was the stuck UI
           * when add succeeded (or looked like it) but cart remove never ran.
           */
          swat.addToWishList(
            p,
            function () {
              invalidateWishlistPageCache();
              notifyWishlistToast(false);
              var removed = removeCartLineFromBtn(button, { skipSfl: true });
              if (!removed) {
                /* Line stayed — show accurate Remove state so a retry can undo. */
                syncMoveUi(button, true);
              }
              clearBusy();
            },
            function () {
              clearBusy();
            }
          );
        }
      });
    }

    window.theme.cartDrawerWishlistRefreshBtn = function (btn) {
      var swat = window.theme._swymCartDrawerWishlistSwat;
      if (!btn || !swat || typeof swat.fetch !== 'function') return;
      var p = payloadFromBtn(btn);
      if (!p.empi || !p.epi) return;
      swat.fetch(function (list) {
        if (btn.matches(MOVE_SEL)) {
          syncMoveUi(btn, itemIsListed(list, p.empi, p.epi));
        } else {
          syncUi(btn, itemIsListed(list, p.empi, p.epi));
        }
      });
    };

    if (window.__aesthetCartWishlistBound) return;
    window.__aesthetCartWishlistBound = true;

    document.addEventListener('click', function (ev) {
      var button =
        ev.target && typeof ev.target.closest === 'function'
          ? ev.target.closest(CLICK_SEL)
          : null;
      if (!button) return;
      if (document.body.getAttribute('data-customer-logged-in') !== 'true') return;
      if (button.classList.contains('is-busy')) return;

      var swat = window.theme._swymCartDrawerWishlistSwat || window._swat;
      if (!swat || typeof swat.fetch !== 'function') return;

      ev.preventDefault();
      ev.stopPropagation();

      if (button.matches(MOVE_SEL)) {
        handleMoveClick(swat, button);
      } else {
        handleHeartClick(swat, button);
      }
    });

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      window.theme._swymCartDrawerWishlistSwat = swat;
      paintAllButtons(swat);

      if (!window.__aesthetCartWishlistMergeBound) {
        window.__aesthetCartWishlistMergeBound = true;
        document.addEventListener('on:cart:after-merge', function () {
          paintAllButtons(window.theme._swymCartDrawerWishlistSwat || swat);
        });
      }
    });
  })();

  /* ===========================================================================
     Swym SDK on demand — "unlock on intent".
     The SDK is consent-gated: with cookies declined (or undecided) it never
     initializes, so every SwymCallbacks-bound handler stays dead. But a
     logged-in customer clicking a wishlist control is requesting an account
     feature, not being tracked — so on that click (or on visiting the wishlist
     page) we wake the SDK via the same mechanism swym-consent-bridge uses:
     ghost-consent key + visitorConsentCollected. Guests never wake it — their
     clicks are intercepted by the login modal before this listener acts.

     Click replay: the original click lands before the SDK exists, so no
     handler is bound yet. We swallow it, wake the SDK, and re-click once the
     SwymCallbacks queue has drained (all binders were pushed at load time, so
     they run before our wake callback — the replayed click hits a live
     handler).
     ========================================================================= */
  (function () {
    'use strict';

    window.theme = window.theme || {};

    var WISHLIST_INTENT_SEL = [
      '[data-pdp-wishlist-toggle]',
      '[data-plp-wishlist-toggle]',
      '[data-cart-swym-wishlist]',
      '[data-cart-move-to-wishlist]',
    ].join(', ');

    function isLoggedIn() {
      return document.body.getAttribute('data-customer-logged-in') === 'true';
    }

    function swatAlive() {
      return !!(window._swat && typeof window._swat.fetch === 'function');
    }

    /* Swym UI (SFL popup, etc.) reads Shopify Customer Privacy, not only the
       ghost localStorage key. Logged-in wake spoofs those getters for this
       page session so Swym treats preferences as allowed without flipping the
       banner. Guests never hit this path. */
    function spoofShopifyConsent() {
      function patch(cp) {
        if (!cp) return;
        var yes = function () {
          return true;
        };
        var granted = function () {
          return {
            analytics: true,
            marketing: true,
            preferences: true,
            sale_of_data: true,
          };
        };
        try {
          cp.preferencesProcessingAllowed = yes;
          cp.analyticsProcessingAllowed = yes;
          if (typeof cp.marketingAllowed === 'function') cp.marketingAllowed = yes;
          if (typeof cp.firstPartyMarketingAllowed === 'function') {
            cp.firstPartyMarketingAllowed = yes;
          }
          if (typeof cp.saleOfDataAllowed === 'function') cp.saleOfDataAllowed = yes;
          if (typeof cp.currentVisitorConsent === 'function') {
            cp.currentVisitorConsent = granted;
          }
        } catch (e) {
          /* ignore */
        }
      }

      patch(window.Shopify && window.Shopify.customerPrivacy);

      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function () {
            patch(window.Shopify && window.Shopify.customerPrivacy);
          }
        );
      }
    }

    function emitSwymConsent(swat) {
      try {
        if (swat && swat.evtLayer && typeof swat.evtLayer.emit === 'function') {
          swat.evtLayer.emit('swym:consent-update');
        }
      } catch (e) {
        /* ignore */
      }
    }

    function wakeSwym() {
      var shop = window.Shopify && window.Shopify.shop;
      if (shop) {
        try {
          localStorage.setItem('swym-ghost-consent-' + shop, 'true');
        } catch (e) {
          /* ignore */
        }
      }
      spoofShopifyConsent();
      try {
        document.dispatchEvent(new CustomEvent('visitorConsentCollected', { bubbles: true }));
      } catch (e) {
        /* ignore */
      }
    }

    function ensureSwymReady(cb) {
      wakeSwym();
      if (swatAlive()) {
        emitSwymConsent(window._swat);
        if (typeof cb === 'function') cb(window._swat);
        return;
      }
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(function (swat) {
        emitSwymConsent(swat);
        if (typeof cb === 'function') cb(swat);
      });
    }

    window.theme.ensureSwymReady = ensureSwymReady;

    /* Wake on click of any wishlist control (logged-in only), then replay. */
    document.addEventListener(
      'click',
      function (e) {
        if (!isLoggedIn() || swatAlive()) return;
        var control =
          e.target && typeof e.target.closest === 'function'
            ? e.target.closest(WISHLIST_INTENT_SEL)
            : null;
        if (!control) return;

        e.preventDefault();
        e.stopPropagation();
        control.classList.add('is-wishlist-waking');

        var wakeTimer = setTimeout(function () {
          control.classList.remove('is-wishlist-waking');
        }, 8000);

        ensureSwymReady(function () {
          clearTimeout(wakeTimer);
          setTimeout(function () {
            control.classList.remove('is-wishlist-waking');
            control.click();
          }, 0);
        });
      },
      true
    );

    /* Wake on the wishlist page itself — visiting it IS the feature request. */
    function wakeForWishlistPage() {
      if (!isLoggedIn()) return;
      if (document.getElementById('swym-wishlist-page')) ensureSwymReady();
    }

    /* Cart / drawer: Save-for-Later is Swym UI that hooks line removal.
       Same ghost-consent wake as logged-in wishlist, including guests who
       declined cookies — opening the bag is the feature request. */
    function wakeForCart() {
      if (
        document.body.classList.contains('template-cart') ||
        document.querySelector('cart-drawer')
      ) {
        ensureSwymReady();
      }
    }

    document.addEventListener('on:cart-drawer:before-open', function () {
      ensureSwymReady();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        wakeForWishlistPage();
        wakeForCart();
      });
    } else {
      wakeForWishlistPage();
      wakeForCart();
    }
  })();

  /* ===========================================================================
     PDP — theme-owned Add to Wishlist button ([data-pdp-wishlist-toggle],
     rendered by main-product.liquid buy-buttons block). Talks to the Swym SDK
     directly, same list semantics as the cart module above. Guests never get
     here: the login-modal intercept swallows their clicks in capture phase.
     epi is read live from the form's input[name="id"] so variant changes
     (which replace .quantity-submit-row__submit wholesale) need no rebinding.
     ========================================================================= */
  (function () {
    'use strict';

    var BTN_SEL = '[data-pdp-wishlist-toggle]';

    if (!document.querySelector(BTN_SEL) && !document.body.classList.contains('template-product')) {
      return;
    }

    function currentEpi(btn) {
      var form = btn.closest('form');
      var input = form ? form.querySelector('input[name="id"]') : null;
      var live = input && input.value ? parseInt(input.value, 10) : 0;
      return live || parseInt(btn.getAttribute('data-epi'), 10) || 0;
    }

    function payloadFromBtn(btn) {
      return {
        empi: parseInt(btn.getAttribute('data-empi'), 10),
        epi: currentEpi(btn),
        du: btn.getAttribute('data-du') || '',
        dt: btn.getAttribute('data-dt') || '',
        iu: btn.getAttribute('data-iu') || '',
        pr: parseInt(btn.getAttribute('data-pr') || '0', 10) || 0,
        qty: 1,
      };
    }

    function itemIsListed(list, empi, epi) {
      if (!list || !list.length) return false;
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (String(it.empi) === String(empi) && String(it.epi) === String(epi)) return true;
      }
      return false;
    }

    function syncBtn(btn, onList) {
      btn.classList.toggle('is-wishlisted', onList);
      btn.setAttribute('aria-pressed', onList ? 'true' : 'false');
      var text = btn.querySelector('.pdp-wishlist-btn__text');
      var label = btn.getAttribute(onList ? 'data-label-added' : 'data-label-add');
      if (text && label) text.textContent = label;
    }

    function setBusy(btn, busy) {
      btn.classList.toggle('is-busy', !!busy);
    }

    function refreshAll(swat) {
      var btns = document.querySelectorAll(BTN_SEL);
      if (!btns.length) return;
      swat.fetch(function (list) {
        for (var i = 0; i < btns.length; i++) {
          var btn = btns[i];
          var p = payloadFromBtn(btn);
          if (!p.empi || !p.epi) continue;
          syncBtn(btn, itemIsListed(list, p.empi, p.epi));
        }
      });
    }

    function toggle(swat, btn) {
      var p = payloadFromBtn(btn);
      if (!p.empi || !p.epi) return;

      setBusy(btn, true);
      var done = function (onList) {
        return function () {
          setBusy(btn, false);
          syncBtn(btn, onList);
          if (window.theme && typeof window.theme.invalidateWishlistPageCache === 'function') {
            window.theme.invalidateWishlistPageCache();
          }
          if (typeof window.aesthetShowWishlistToast === 'function') {
            window.aesthetShowWishlistToast(!onList);
          }
        };
      };
      var fail = function () {
        setBusy(btn, false);
      };

      swat.fetch(function (list) {
        if (itemIsListed(list, p.empi, p.epi)) {
          swat.removeFromWishList({ empi: p.empi, epi: p.epi, du: p.du }, done(false), fail);
        } else {
          swat.addToWishList(p, done(true), fail);
        }
      });
    }

    var bound = false;

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      if (bound || window.__aesthetPdpWishlistBound) return;
      bound = true;
      window.__aesthetPdpWishlistBound = true;

      refreshAll(swat);

      document.addEventListener('on:variant:change', function () {
        setTimeout(function () {
          refreshAll(swat);
        }, 50);
      });

      document.addEventListener('click', function (e) {
        var btn =
          e.target && typeof e.target.closest === 'function' ? e.target.closest(BTN_SEL) : null;
        if (!btn) return;
        if (document.body.getAttribute('data-customer-logged-in') !== 'true') return;
        if (btn.classList.contains('is-busy')) return;

        e.preventDefault();
        toggle(swat, btn);
      });
    });
  })();

  /* ===========================================================================
     PLP — theme-owned wishlist hearts ([data-plp-wishlist-toggle], rendered by
     product-block.liquid on every product tile). Replaces the consent-gated
     Swym Collections Advanced embed. Tiles arrive dynamically (infinite
     scroll, AJAX filters, recommendation carousels), so state is painted from
     a cached list whenever the DOM grows. Guests hit the login modal
     intercept; logged-in declined-cookies users go through the wake-on-intent
     module — both in capture phase, before this delegated handler.
     ========================================================================= */
  (function () {
    'use strict';

    var BTN_SEL = '[data-plp-wishlist-toggle]';

    function payloadFromBtn(btn) {
      return {
        empi: parseInt(btn.getAttribute('data-empi'), 10),
        epi: parseInt(btn.getAttribute('data-epi'), 10),
        du: btn.getAttribute('data-du') || '',
        dt: btn.getAttribute('data-dt') || '',
        iu: btn.getAttribute('data-iu') || '',
        pr: parseInt(btn.getAttribute('data-pr') || '0', 10) || 0,
        qty: 1,
      };
    }

    function itemIsListed(list, empi, epi) {
      if (!list || !list.length) return false;
      var empiStr = String(empi);
      var epiStr = String(epi);
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (!it) continue;
        var itEmpi = String(
          it.empi != null ? it.empi : it.product_id != null ? it.product_id : ''
        );
        var itEpi = String(
          it.epi != null ? it.epi : it.variant_id != null ? it.variant_id : ''
        );
        if (itEmpi === empiStr && itEpi === epiStr) return true;
      }
      return false;
    }

    var lastList = null;

    function syncBtn(btn, onList) {
      onList = !!onList;
      if (btn.classList.contains('is-wishlisted') === onList) {
        btn.setAttribute('aria-pressed', onList ? 'true' : 'false');
        return;
      }
      btn.classList.toggle('is-wishlisted', onList);
      btn.setAttribute('aria-pressed', onList ? 'true' : 'false');
    }

    function patchLocalList(empi, epi, shouldBeOn) {
      var list = lastList ? lastList.slice() : [];
      var empiStr = String(empi);
      var epiStr = String(epi);
      if (shouldBeOn) {
        if (!itemIsListed(list, empi, epi)) {
          list.push({ empi: empi, epi: epi });
        }
      } else {
        list = list.filter(function (it) {
          if (!it) return false;
          return !(String(it.empi) === empiStr && String(it.epi) === epiStr);
        });
      }
      lastList = list;
    }

    function paintAll() {
      if (!lastList) return;
      var btns = document.querySelectorAll(BTN_SEL);
      for (var i = 0; i < btns.length; i++) {
        var p = payloadFromBtn(btns[i]);
        if (!p.empi || !p.epi) continue;
        /* Don’t fight an in-flight click */
        if (btns[i].classList.contains('is-busy')) continue;
        syncBtn(btns[i], itemIsListed(lastList, p.empi, p.epi));
      }
    }

    var paintTimer = null;

    function schedulePaint() {
      if (paintTimer) return;
      paintTimer = setTimeout(function () {
        paintTimer = null;
        paintAll();
      }, 150);
    }

    var bound = false;

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      if (bound || window.__aesthetPlpWishlistBound) return;
      bound = true;
      window.__aesthetPlpWishlistBound = true;

      function refetch() {
        swat.fetch(function (list) {
          lastList = list || [];
          paintAll();
        });
      }

      refetch();

      /* Paint tiles added later (infinite scroll, carousels, AJAX filters)
         from the cached list — childList only, so our own class toggles never
         re-trigger it. */
      var mo = new MutationObserver(schedulePaint);
      mo.observe(document.body, { childList: true, subtree: true });

      document.addEventListener('click', function (e) {
        var btn =
          e.target && typeof e.target.closest === 'function' ? e.target.closest(BTN_SEL) : null;
        if (!btn) return;
        if (document.body.getAttribute('data-customer-logged-in') !== 'true') return;
        if (btn.classList.contains('is-busy')) return;

        e.preventDefault();
        e.stopPropagation();

        var p = payloadFromBtn(btn);
        if (!p.empi || !p.epi) return;

        var wasOn = btn.classList.contains('is-wishlisted');
        if (lastList) {
          wasOn = itemIsListed(lastList, p.empi, p.epi);
        }
        var nextOn = !wasOn;

        btn.classList.add('is-busy');
        var busyTimer = setTimeout(function () {
          btn.classList.remove('is-busy');
        }, 6000);

        var clearBusy = function () {
          clearTimeout(busyTimer);
          btn.classList.remove('is-busy');
        };

        /* Optimistic UI + local list so MutationObserver paints don’t flicker */
        syncBtn(btn, nextOn);
        patchLocalList(p.empi, p.epi, nextOn);
        if (typeof window.aesthetShowWishlistToast === 'function') {
          window.aesthetShowWishlistToast(wasOn);
        }

        var finishOk = function () {
          clearBusy();
          if (window.theme && typeof window.theme.invalidateWishlistPageCache === 'function') {
            window.theme.invalidateWishlistPageCache();
          }
          refetch();
        };
        var finishFail = function () {
          syncBtn(btn, wasOn);
          patchLocalList(p.empi, p.epi, wasOn);
          clearBusy();
          refetch();
        };

        if (wasOn) {
          swat.removeFromWishList(
            { empi: p.empi, epi: p.epi, du: p.du },
            finishOk,
            finishFail
          );
        } else {
          swat.addToWishList(p, finishOk, finishFail);
        }
      });
    });
  })();

  (function () {
    function toInt(value) {
      var parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    function getPriorityValue(node) {
      return node.dataset.outletSeasonPriority === '1' ? 1 : 0;
    }

    function getPublishedAtValue(node) {
      return toInt(node.dataset.outletPublishedAt);
    }

    function getStableOriginalIndex(node, fallbackIndex) {
      if (!node.dataset.outletOriginalIndex) {
        node.dataset.outletOriginalIndex = String(fallbackIndex);
      }
      return toInt(node.dataset.outletOriginalIndex);
    }

    // Season boost only when browsing Featured/manual with no filters applied.
    // Any sort_by or filter.* must keep Shopify's server order.
    function urlHasCollectionFilters() {
      try {
        var params = new URLSearchParams(window.location.search);
        var keys = params.keys();
        var key = keys.next();
        while (!key.done) {
          if (String(key.value).indexOf('filter.') === 0) return true;
          key = keys.next();
        }
      } catch (e) {
        return window.location.search.indexOf('filter.') !== -1;
      }
      return false;
    }

    function shouldApplyOutletSeasonSort(sectionEl) {
      if (urlHasCollectionFilters()) return false;

      var activeFilters = 0;
      if (sectionEl && sectionEl.getAttribute) {
        activeFilters = toInt(sectionEl.getAttribute('data-collection-active-filters'));
      }
      if (activeFilters > 0) return false;

      var sortBy = '';
      if (sectionEl && sectionEl.getAttribute) {
        sortBy = sectionEl.getAttribute('data-collection-sort-by') || '';
      }
      var urlSort = '';
      try {
        urlSort = new URLSearchParams(window.location.search).get('sort_by') || '';
      } catch (e) {
        urlSort = '';
      }
      // Prefer explicit URL sort (shareable links / AJAX filter updates)
      if (urlSort) sortBy = urlSort;
      return !sortBy || sortBy === 'manual';
    }

    function sortOutletGrid(grid) {
      if (!grid) return;

      var productNodes = Array.from(grid.children).filter(function (child) {
        return child && child.tagName && child.tagName.toLowerCase() === 'product-block';
      });

      if (productNodes.length < 2) return;

      productNodes.forEach(function (node, index) {
        getStableOriginalIndex(node, index);
      });

      var sortedNodes = productNodes.slice().sort(function (a, b) {
        var priorityDiff = getPriorityValue(b) - getPriorityValue(a);
        if (priorityDiff !== 0) return priorityDiff;

        var dateDiff = getPublishedAtValue(b) - getPublishedAtValue(a);
        if (dateDiff !== 0) return dateDiff;

        return getStableOriginalIndex(a, 0) - getStableOriginalIndex(b, 0);
      });

      var orderChanged = sortedNodes.some(function (node, index) {
        return productNodes[index] !== node;
      });

      if (!orderChanged) return;

      sortedNodes.forEach(function (node) {
        grid.appendChild(node);
      });
    }

    function runOutletSort(scope) {
      var root = scope && scope.querySelector ? scope : document;
      var sections = root.querySelectorAll('[data-outlet-season-sort-enabled="true"]');
      if (!sections.length) return;

      sections.forEach(function (sectionEl) {
        if (!shouldApplyOutletSeasonSort(sectionEl)) return;
        sectionEl.querySelectorAll('.collection-listing .product-grid').forEach(sortOutletGrid);
      });
    }

    var scheduled = null;
    function scheduleOutletSort(scope) {
      if (scheduled) {
        cancelAnimationFrame(scheduled);
      }
      scheduled = requestAnimationFrame(function () {
        scheduled = null;
        runOutletSort(scope);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        runOutletSort(document);
      });
    } else {
      runOutletSort(document);
    }

    document.addEventListener('shopify:section:load', function (e) {
      if (e && e.target) {
        scheduleOutletSort(e.target);
      } else {
        scheduleOutletSort(document);
      }
    });

    var observer = new MutationObserver(function (mutations) {
      var shouldResort = mutations.some(function (mutation) {
        if (!mutation || !mutation.target || !mutation.target.closest) return false;
        return !!mutation.target.closest('[data-outlet-season-sort-enabled="true"] .collection-listing .product-grid');
      });

      if (shouldResort) {
        scheduleOutletSort(document);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();

  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.

  // Multi-word option values (e.g. One Size) → uppercase in dynamic/AJAX UI
  // variant-picker.js uses formatMultiWordAllCapsDisplay when updating labels from input.value.
  const OPTION_VALUE_SELECTORS = [
    '.opt-label .js-value',                  // variant option buttons (PDP, quickbuy, product tiles)
    '.option-selector__label-value',         // selected value shown in label header
    '.js-size-text',                         // size label inline text
    '.js-color-text',                        // colour label inline text
    '.cart-item__variant-value',             // cart drawer / cart page line items
    '.product-block-options__item__text',    // product card hover overlay options
  ].join(', ');

  function uppercaseMultiWordOptionValues(root) {
    (root || document).querySelectorAll(OPTION_VALUE_SELECTORS).forEach(el => {
      const text = el.textContent.trim();
      if (text.includes(' ')) {
        el.textContent = text.toUpperCase();
      }
    });
  }

  uppercaseMultiWordOptionValues();

  // Re-run when dynamic content is injected (cart drawer open, quickbuy, variant swap, etc.)
  document.addEventListener('cc:variant-changed', () => uppercaseMultiWordOptionValues());
  document.addEventListener('cc:product-content-updated', () => uppercaseMultiWordOptionValues());
  document.addEventListener('cc:cart-drawer-opened', () => uppercaseMultiWordOptionValues());
  document.addEventListener('cc:cart-updated', () => uppercaseMultiWordOptionValues());

  // Catch any other dynamic injections via MutationObserver
  new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) uppercaseMultiWordOptionValues(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // PDP video: poster-click → open gallery on the video; gallery-viewer video injection
  (function () {

    // ---------- shared video helpers ----------

    function buildGalleryVideo(thumb) {
      var tmpl = thumb.querySelector('template.js-gallery-video-sources');
      var sources = tmpl ? Array.from(tmpl.content.querySelectorAll('source')) : [];
      if (!sources.length) return null;

      var video = document.createElement('video');
      video.className = 'gallery-viewer__zoom-video';
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.loop = true;
      video.setAttribute('preload', 'auto');
      var posterUrl = thumb.getAttribute('data-zoom-url');
      if (posterUrl) video.setAttribute('poster', posterUrl);
      sources.forEach(function (src) {
        var s = document.createElement('source');
        s.src = src.src;
        s.type = src.type;
        video.appendChild(s);
      });
      return video;
    }

    function injectGalleryVideo(thumb, gallery) {
      var zoomContainer = gallery.querySelector('.gallery-viewer__zoom-container');
      if (!zoomContainer) return;
      var existing = zoomContainer.querySelector('.gallery-viewer__zoom-video');
      if (existing) { existing.pause(); existing.remove(); }
      var img = zoomContainer.querySelector('.gallery-viewer__zoom-image');
      var video = buildGalleryVideo(thumb);
      if (!video) return;
      // Blank video frames often paint white before decode — keep the preview image
      // visible underneath until we have real pixels, then fade the video in.
      video.style.opacity = '0';
      zoomContainer.appendChild(video);
      var revealed = false;
      function reveal() {
        if (revealed) return;
        revealed = true;
        video.style.opacity = '';
        if (img) img.style.visibility = 'hidden';
      }
      video.addEventListener('loadeddata', reveal, { once: true });
      video.addEventListener('playing', reveal, { once: true });
      video.addEventListener('error', reveal, { once: true });
      setTimeout(reveal, 2800);
    }

    function clearGalleryVideo(gallery) {
      var zoomContainer = gallery.querySelector('.gallery-viewer__zoom-container');
      if (!zoomContainer) return;
      var existing = zoomContainer.querySelector('.gallery-viewer__zoom-video');
      if (existing) { existing.pause(); existing.remove(); }
      var img = zoomContainer.querySelector('.gallery-viewer__zoom-image');
      if (img) img.style.visibility = '';
    }

    /** After gallery-viewer moves to another thumb (arrows, keyboard, etc.), sync video. */
    function syncGalleryVideoWithActiveThumb(gallery) {
      if (!gallery) return;
      /* Modal thumbs ALSO get `is-active` from MediaGallery (PDP sync). GalleryViewer
         uses `gallery-viewer__thumb--active` for in-modal navigation only. Matching
         `.gallery-viewer__thumb--video.is-active` wrongly re-injects video while the
         zoom view shows an image (PDP can still be on a video). */
      var activeThumb = gallery.querySelector('.gallery-viewer__thumb.gallery-viewer__thumb--active');
      if (activeThumb && activeThumb.classList.contains('gallery-viewer__thumb--video')) {
        injectGalleryVideo(activeThumb, gallery);
      } else {
        clearGalleryVideo(gallery);
      }
      scheduleGalleryNavArrowPosition(gallery);
    }

    var gvArrowGap = 10;

    function getGalleryNavMedia(gallery) {
      var img = gallery.querySelector('.gallery-viewer__zoom-image');
      if (img && img.style.visibility !== 'hidden') return img;
      return gallery.querySelector('.gallery-viewer__zoom-video');
    }

    function positionGalleryNavArrows(gallery) {
      if (!gallery) return;
      var prev = gallery.querySelector('.gallery-viewer__prev');
      var next = gallery.querySelector('.gallery-viewer__next');
      var media = getGalleryNavMedia(gallery);
      if (!prev || !next) return;

      if (!media || !media.offsetParent) {
        gallery.style.removeProperty('--gv-arrow-prev-left');
        gallery.style.removeProperty('--gv-arrow-next-left');
        gallery.style.removeProperty('--gv-arrow-top');
        return;
      }

      var viewerRect = gallery.getBoundingClientRect();
      var mediaRect = media.getBoundingClientRect();
      var visibleLeft = Math.max(mediaRect.left, viewerRect.left);
      var visibleRight = Math.min(mediaRect.right, viewerRect.right);
      var visibleTop = Math.max(mediaRect.top, viewerRect.top);
      var visibleBottom = Math.min(mediaRect.bottom, viewerRect.bottom);

      if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
        return;
      }

      var visibleLeftRel = visibleLeft - viewerRect.left;
      var visibleRightRel = visibleRight - viewerRect.left;
      var visibleCenterY = ((visibleTop + visibleBottom) / 2) - viewerRect.top;
      var prevLeft = visibleLeftRel - prev.offsetWidth - gvArrowGap;
      var nextLeft = visibleRightRel + gvArrowGap;
      var minLeft = 8;

      if (window.matchMedia('(min-width: 1000px)').matches) {
        var thumbRail = parseFloat(getComputedStyle(gallery).getPropertyValue('--gv-thumb-rail'));
        if (!isNaN(thumbRail)) minLeft = thumbRail + 8;
      }

      prevLeft = Math.max(minLeft, prevLeft);
      nextLeft = Math.min(viewerRect.width - next.offsetWidth - 8, nextLeft);
      nextLeft = Math.max(prevLeft + prev.offsetWidth + gvArrowGap, nextLeft);

      gallery.style.setProperty('--gv-arrow-prev-left', prevLeft + 'px');
      gallery.style.setProperty('--gv-arrow-next-left', nextLeft + 'px');
      gallery.style.setProperty('--gv-arrow-top', (visibleCenterY - (prev.offsetHeight / 2)) + 'px');
    }

    function scheduleGalleryNavArrowPosition(gallery) {
      if (!gallery) return;
      positionGalleryNavArrows(gallery);
      requestAnimationFrame(function () {
        positionGalleryNavArrows(gallery);
        requestAnimationFrame(function () {
          positionGalleryNavArrows(gallery);
        });
      });
      setTimeout(function () { positionGalleryNavArrows(gallery); }, 120);
      setTimeout(function () { positionGalleryNavArrows(gallery); }, 350);
    }

    function ensureGalleryNavArrowPositioning(gallery) {
      if (!gallery || gallery.dataset.gvArrowsBound === 'true') return;
      gallery.dataset.gvArrowsBound = 'true';

      new ResizeObserver(function () {
        positionGalleryNavArrows(gallery);
      }).observe(gallery);

      var zoomContainer = gallery.querySelector('.gallery-viewer__zoom-container');
      if (!zoomContainer) return;

      new MutationObserver(function () {
        scheduleGalleryNavArrowPosition(gallery);
        var img = zoomContainer.querySelector('.gallery-viewer__zoom-image');
        if (img && img.dataset.gvArrowLoadBound !== 'true') {
          img.dataset.gvArrowLoadBound = 'true';
          img.addEventListener('load', function () {
            scheduleGalleryNavArrowPosition(gallery);
          });
        }
      }).observe(zoomContainer, { childList: true, subtree: true });
    }

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'open') return;
        var modal = mutation.target;
        if (!modal.classList || !modal.classList.contains('gallery-viewer-modal')) return;
        if (!modal.hasAttribute('open')) return;
        var gallery = modal.querySelector('gallery-viewer');
        if (!gallery) return;
        ensureGalleryNavArrowPositioning(gallery);
        scheduleGalleryNavArrowPosition(gallery);
      });
    }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });

    // ---------- PDP gallery open: keep main carousel still behind the modal ----------
    // Same issue for image zoom (theme's .show-gallery) and video poster (custom).
    // See video-click handler below for the full comment on scroll / loop / body width.

    function prepareQuietCarouselForModal(mediaGallery) {
      var mainCarousel = mediaGallery.querySelector('.main-image carousel-slider');
      var sliderEl = mainCarousel && mainCarousel.querySelector('.slider');
      var activeSlide = mainCarousel && mainCarousel.querySelector('.slider__item.is-active');
      var savedScrollHandler = mainCarousel && mainCarousel.scrollHandler;
      if (sliderEl && savedScrollHandler) {
        sliderEl.removeEventListener('scroll', savedScrollHandler);
      }
      if (sliderEl) {
        sliderEl.style.scrollSnapType = 'none';
      }
      return { sliderEl: sliderEl, activeSlide: activeSlide, savedScrollHandler: savedScrollHandler };
    }

    function syncCarouselScrollToActiveQuiet(state) {
      if (state && state.sliderEl && state.activeSlide) {
        state.sliderEl.scrollLeft = state.activeSlide.offsetLeft;
      }
    }

    function runDeferredCarouselScrollCorrections(state) {
      if (!state) return;
      setTimeout(function () {
        syncCarouselScrollToActiveQuiet(state);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            syncCarouselScrollToActiveQuiet(state);
          });
        });
      }, 0);
    }

    function restoreCarouselScrollListenerAfterModal(state) {
      if (!state) return;
      setTimeout(function () {
        syncCarouselScrollToActiveQuiet(state);
        if (state.sliderEl) state.sliderEl.style.scrollSnapType = '';
        if (state.sliderEl && state.savedScrollHandler) {
          state.sliderEl.addEventListener('scroll', state.savedScrollHandler);
        }
      }, 450);
    }

    /** After gallery modal closes: body width grows again (scrollbar back) — same snap/reflow
     *  as on open — and Modal.close() always calls pauseAllMedia() (PDP video stays paused).
     *  Lock carousel before removeAttribute('open'), then correct scroll + resume main video. */
    function resumeMainGalleryVideoIfAny(mediaGallery) {
      if (!mediaGallery) return;
      var slide = mediaGallery.querySelector('.main-image .slider__item.is-active');
      if (!slide) return;
      var video = slide.querySelector('video');
      if (!video) return;
      video.play().catch(function () {});
    }

    function scheduleRecoverAfterGalleryModalWillClose(mediaGallery) {
      if (!mediaGallery) return;
      var state = prepareQuietCarouselForModal(mediaGallery);
      setTimeout(function () {
        syncCarouselScrollToActiveQuiet(state);
        runDeferredCarouselScrollCorrections(state);
        restoreCarouselScrollListenerAfterModal(state);
        resumeMainGalleryVideoIfAny(mediaGallery);
      }, 0);
    }

    document.addEventListener('click', function (e) {
      var modal = e.target.closest('.gallery-viewer-modal');
      if (!modal || !modal.hasAttribute('open')) return;
      if (e.target !== modal && !e.target.closest('.js-close-modal')) return;
      var opener = modal.openedBy;
      var mediaGallery = opener && opener.closest('media-gallery');
      if (!mediaGallery) return;
      scheduleRecoverAfterGalleryModalWillClose(mediaGallery);
    }, true);

    document.addEventListener('keyup', function (e) {
      if (e.key !== 'Escape') return;
      var modal = document.querySelector('.gallery-viewer-modal[open]');
      if (!modal) return;
      var opener = modal.openedBy;
      var mediaGallery = opener && opener.closest('media-gallery');
      if (!mediaGallery) return;
      scheduleRecoverAfterGalleryModalWillClose(mediaGallery);
    }, true);

    // Image zoom: theme opens the modal in the bubble phase — lock carousel in capture first.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a.show-gallery');
      if (!link || link.classList.contains('js-open-gallery-video')) return;
      var mediaGallery = link.closest('media-gallery');
      if (!mediaGallery || !mediaGallery.hasAttribute('data-zoom-enabled')) return;

      var state = prepareQuietCarouselForModal(mediaGallery);
      runDeferredCarouselScrollCorrections(state);
      restoreCarouselScrollListenerAfterModal(state);
    }, true);

    // ---------- poster-click → open gallery on the video ----------

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('.js-open-gallery-video');
      if (!opener) return;

      e.preventDefault();

      var mediaId = opener.getAttribute('data-video-media-id');
      if (!mediaId) return;

      var mediaGallery = opener.closest('media-gallery');
      // Use MediaGallery's own cached modal so its thumb/state sync stays correct.
      var galleryModal = mediaGallery && mediaGallery.galleryModal;
      if (!galleryModal) return;

      var state = prepareQuietCarouselForModal(mediaGallery);

      // Mirror what MediaGallery.openGalleryViewer does so init runs properly
      if (!galleryModal.parentElement) {
        document.body.appendChild(galleryModal);
      }

      var viewer = galleryModal.querySelector('gallery-viewer');
      var videoThumb = viewer && viewer.querySelector(
        '.gallery-viewer__thumb--video[data-media-id="' + mediaId + '"]'
      );

      // Open the modal via the proper Modal.open() API (sets open="", traps focus)
      if (typeof galleryModal.open === 'function') {
        galleryModal.open(opener);
      } else {
        galleryModal.setAttribute('open', '');
      }
      galleryModal.classList.remove('invisible');

      // Correct the carousel's scrollLeft synchronously now that the body width
      // has changed (open="" applied above). Accessing offsetLeft forces layout.
      syncCarouselScrollToActiveQuiet(state);
      runDeferredCarouselScrollCorrections(state);
      restoreCarouselScrollListenerAfterModal(state);

      // Initialise gallery-viewer on the video thumb's zoom URL. selectThumb()
      // sets up currentZoomImage AND active classes — without this the gallery
      // is in a broken half-initialised state.
      if (viewer && typeof viewer.init === 'function') {
        viewer.init(videoThumb ? videoThumb.getAttribute('data-zoom-url') : null);
        if (typeof viewer.focus === 'function') viewer.focus();
      }

      // Inject the video on top of the preview image. Also clear the loading
      // class immediately so the spinner doesn't flash before the video paints.
      if (viewer && videoThumb) {
        var zc = viewer.querySelector('.gallery-viewer__zoom-container');
        if (zc) zc.classList.remove('gallery-viewer__zoom-container--loading');
        injectGalleryVideo(videoThumb, viewer);
        ensureGalleryNavArrowPositioning(viewer);
        scheduleGalleryNavArrowPosition(viewer);
      }
    });

    // ---------- gallery-viewer video thumbnail click ----------

    // Direct click on a video thumbnail — let gallery-viewer run first (shows preview
    // image via data-zoom-url), then replace the zoom content with the actual video.
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('.gallery-viewer__thumb--video');
      if (!thumb) return;
      var gallery = thumb.closest('gallery-viewer');
      if (!gallery) return;
      setTimeout(function () { injectGalleryVideo(thumb, gallery); }, 0);
      setTimeout(function () {
        ensureGalleryNavArrowPositioning(gallery);
        scheduleGalleryNavArrowPosition(gallery);
      }, 50);
    });

    // Prev/next UI — after gallery-viewer changes the active thumb, inject or clear video
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.gallery-viewer__prev, .gallery-viewer__next');
      if (!btn) return;
      var gallery = btn.closest('gallery-viewer');
      if (!gallery) return;

      setTimeout(function () {
        syncGalleryVideoWithActiveThumb(gallery);
      }, 50);
    });

    // Keyboard ← / → calls selectPreviousThumb / selectNextThumb on gallery-viewer
    // (no click on prev/next). Use the open modal so we still sync when focus is on
    // the modal shell rather than inside gallery-viewer.
    document.addEventListener('keyup', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var modal = document.querySelector('.gallery-viewer-modal[open]');
      if (!modal) return;
      var gallery = modal.querySelector('gallery-viewer');
      if (!gallery) return;
      setTimeout(function () {
        syncGalleryVideoWithActiveThumb(gallery);
      }, 0);
    });

  })();

  /* Cart template: rich-text USPs use "|" in one paragraph — split into bordered chips without editor rework */
  (function () {
    function resolveCartUspSection(scope) {
      var root = scope && scope.nodeType === 1 ? scope : document.documentElement;
      var el =
        root.id === 'shopify-section-rich_text_733Etr'
          ? root
          : root.querySelector && root.querySelector('#shopify-section-rich_text_733Etr');
      if (el) return el;
      if (!document.body.classList.contains('template-cart')) return null;
      var rich = document.querySelectorAll('.shopify-section.section-rich-text');
      return rich.length >= 2 ? rich[rich.length - 1] : null;
    }

    function enhanceCartUspChips(scope) {
      var sectionRoot = resolveCartUspSection(scope);
      if (!sectionRoot) return;
      sectionRoot.classList.add('cart-usp-section');

      sectionRoot.querySelectorAll('.rte').forEach(function (rte) {
        if (rte.getAttribute('data-usp-chips') === 'done') return;
        var changed = false;
        rte.querySelectorAll(':scope > p').forEach(function (p) {
          var raw = p.innerHTML;
          if (raw.indexOf('|') === -1) return;
          var parts = raw.split('|').map(function (s) {
            return s.replace(/\s+/g, ' ').trim();
          }).filter(Boolean);
          if (parts.length < 2) return;
          var wrap = document.createElement('span');
          wrap.className = 'cart-usp-chips';
          wrap.style.setProperty('--usp-cols', String(parts.length));
          parts.forEach(function (part) {
            var chip = document.createElement('span');
            chip.className = 'cart-usp-chip';
            chip.innerHTML = part;
            wrap.appendChild(chip);
          });
          p.textContent = '';
          p.appendChild(wrap);
          changed = true;
        });
        if (changed) rte.setAttribute('data-usp-chips', 'done');
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        enhanceCartUspChips(document.documentElement);
      });
    } else {
      enhanceCartUspChips(document.documentElement);
    }

    document.addEventListener('shopify:section:load', function (e) {
      var target = e.target;
      if (!target || !target.querySelector) return;
      enhanceCartUspChips(target);
    });
  })();

  // PLP tile video: autoplay when the tile is in the viewport, pause when out
  (function () {
    function initTileVideos(root) {
      (root || document).querySelectorAll('.product-block__tile-video').forEach(function (video) {
        if (video._tileVideoInit) return;
        video._tileVideoInit = true;

        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              video.play().catch(function () {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.1 });

        observer.observe(video);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initTileVideos(); });
    } else {
      initTileVideos();
    }

    document.addEventListener('shopify:section:load', function (e) {
      initTileVideos(e.target);
    });
  })();

  /**
   * PDP Klarna OSM — hide in Greece/Germany when variant price is at or above the
   * per-market threshold set in sections/main-product.liquid (data-klarna-max-cents).
   */
  (function () {
    function getKlarnaBlock(scope) {
      var root = scope || document;
      return root.querySelector('[data-pdp-klarna-conditional]');
    }

    function syncKlarnaBlock(block, variantPrice) {
      if (!block) return;
      var maxCents = parseInt(block.dataset.klarnaMaxCents, 10);
      if (!maxCents || maxCents < 0) return;
      var price = variantPrice != null ? variantPrice : 0;
      if (price >= maxCents) {
        block.setAttribute('hidden', '');
      } else {
        block.removeAttribute('hidden');
      }
    }

    function onVariantChange(e) {
      var section = e.target && e.target.closest
        ? e.target.closest('.section-main-product')
        : null;
      if (!section) return;
      var variant = e.detail && e.detail.variant;
      syncKlarnaBlock(getKlarnaBlock(section), variant ? variant.price : null);
    }

    document.addEventListener('on:variant:change', onVariantChange);
  })();
})();

/**
 * PDP: Add to Wishlist beside the buy button — in stock AND sold out.
 *
 * Swym's "Button position: Default" renders #swym-atw-button-container inside its
 * own app block (in both stock states). We move it into the STABLE
 * .quantity-submit-row wrapper — NOT the inner .quantity-submit-row__submit,
 * which the theme fully replaces on every variant change (variant-picker.js
 * → toReplace.replaceWith). Living in the stable wrapper, the wishlist persists
 * across variant changes and sits beside whichever buy button is shown (Add to
 * Bag or Notify me). CSS in custom.css handles the side-by-side layout.
 *
 * Self-healing: if Swym re-creates its button in the app block (e.g. on a
 * variant change), we move the fresh one into the row and drop any stale copy.
 * Do-no-harm: if Swym hasn't rendered a wishlist button, nothing moves.
 */
(function () {
  'use strict';

  var MAIN = '.section-main-product';
  var CONTAINER_SEL = '#swym-atw-button-container, .swym-atw-button-container';
  var HOME_SEL = '.shopify-app-block, .pdp-app-block';

  // NOTE: the main PDP <product-form> itself carries the class "quickbuy-content"
  // (it is the markup Swym/quick-buy clones), so we must NOT treat that class as a
  // quick-buy marker — doing so excluded the real PDP row. The actual quick-buy /
  // restock instances live inside one of these dedicated modal/portal wrappers.
  function inQuickBuy(el) {
    return !!(
      el &&
      el.closest &&
      el.closest('.quickbuy-container, .mobile-quick-add-modal, #plp-restock-notify-portal')
    );
  }

  function getBuyWrappers() {
    return Array.prototype.filter.call(
      document.querySelectorAll(MAIN + ' .product-info .quantity-submit-row'),
      function (row) {
        return !inQuickBuy(row);
      }
    );
  }

  function isContainer(el) {
    return el && el.matches && el.matches(CONTAINER_SEL);
  }

  function placeWishlist() {
    getBuyWrappers().forEach(function (row) {
      var productInfo = row.closest('.product-info') || document;

      // Swym's freshly-rendered button still sits in its app block ("home").
      var home = Array.prototype.filter.call(
        productInfo.querySelectorAll(CONTAINER_SEL),
        function (c) {
          return !inQuickBuy(c) && c.closest(HOME_SEL);
        }
      )[0];

      var existing = Array.prototype.filter.call(row.children, isContainer)[0];

      if (home) {
        // Move the fresh one into the stable wrapper; drop any stale copy.
        if (existing && existing !== home) existing.remove();
        var submit = row.querySelector(':scope > .quantity-submit-row__submit');
        if (home.parentElement !== row) row.insertBefore(home, submit || row.firstChild);
      }
      // else: already relocated (lives in the wrapper) — leave it in place.
    });
  }

  var frame = null;
  function scheduleSync() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      try {
        placeWishlist();
      } catch (e) {
        /* no-op */
      }
    });
  }

  function init() {
    scheduleSync();
    // Swym renders asynchronously — retry a few times after initial load.
    [400, 1000, 2000, 3500].forEach(function (t) {
      setTimeout(scheduleSync, t);
    });

    // Variant change swaps the inner submit row (and may re-render Swym's button).
    document.addEventListener('on:variant:change', function () {
      [50, 300, 800, 1500].forEach(function (t) {
        setTimeout(scheduleSync, t);
      });
    });

    var root = document.querySelector(MAIN + ' .product-info') || document.querySelector(MAIN);
    if (root) {
      new MutationObserver(scheduleSync).observe(root, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Size guide contact links: open the LiveChat widget (Text/LiveChat app).
 */
(function () {
  function openLiveChat() {
    if (window.LiveChatWidget && typeof window.LiveChatWidget.call === 'function') {
      window.LiveChatWidget.call('maximize');
      return true;
    }

    if (window.LC_API && typeof window.LC_API.open_chat_window === 'function') {
      window.LC_API.open_chat_window();
      return true;
    }

    return false;
  }

  function closeSizeChartModal(link) {
    var modal = link.closest('modal-dialog.modal--size-chart');
    if (modal && typeof modal.close === 'function') {
      modal.close();
    }
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href="#livechat"]');
    if (!link) return;

    event.preventDefault();
    closeSizeChartModal(link);

    if (openLiveChat()) return;

    if (window.LiveChatWidget && typeof window.LiveChatWidget.on === 'function') {
      window.LiveChatWidget.on('ready', function () {
        openLiveChat();
      });
    }
  });
})();

/**
 * Re-evaluate automatic cart discounts when customer login state changes.
 * Shopify keeps line keys/qty but may not re-run Functions until the cart is updated
 * (logout → login with existing lines is a common miss; remove/re-add fixes it).
 */
(function () {
  const STORAGE_KEY = 'aesthet_cart_customer_id';

  function refreshCartAfterCustomerChange() {
    const routes = window.theme && window.theme.routes;
    const cartUrl = (routes && routes.cart) || '/cart';
    const cartUpdateUrl = (routes && routes.cartUpdate) || '/cart/update.js';

    fetch(cartUrl + '.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP error ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) return null;

        const updates = {};
        cart.items.forEach(function (item) {
          updates[item.key] = item.quantity;
        });

        return fetch(cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ updates: updates }),
        });
      })
      .then(function (response) {
        if (!response) return;
        if (!response.ok) throw new Error('HTTP error ' + response.status);

        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
        document.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true, cancelable: false })
        );
      })
      .catch(function (error) {
        console.log(error.message);
      });
  }

  function initCustomerCartRefresh() {
    const body = document.body;
    if (!body) return;

    const currentCustomerId = body.getAttribute('data-customer-id') || '';
    const previousCustomerId = sessionStorage.getItem(STORAGE_KEY);

    if (previousCustomerId === currentCustomerId) return;

    sessionStorage.setItem(STORAGE_KEY, currentCustomerId);

    // First anonymous visit this tab — nothing to re-price yet.
    if (previousCustomerId === null && currentCustomerId === '') return;

    refreshCartAfterCustomerChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomerCartRefresh);
  } else {
    initCustomerCartRefresh();
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) initCustomerCartRefresh();
  });
})();

/**
 * Designer PLP top filters: after clicking a carousel filter link, open the
 * left filter panel and scroll so the top-filter section sits under the sticky header.
 * No page-hide — early scroll + a short refine only.
 */
(function () {
  const STORAGE_KEY = 'aesthet:openFiltersFromTopFilter';
  const SCROLL_KEY = 'aesthet:openFiltersFromTopFilterY';

  function hrefHasFilterParam(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      for (const key of url.searchParams.keys()) {
        if (key.indexOf('filter.') === 0) return true;
      }
    } catch (e) {
      return href.indexOf('filter.') !== -1;
    }
    return false;
  }

  function currentUrlHasFilterParam() {
    return hrefHasFilterParam(window.location.href);
  }

  function stickyHeaderHeight() {
    let h = 0;
    if (window.theme && typeof theme.stickyHeaderHeight === 'function') {
      h = theme.stickyHeaderHeight() || 0;
    }
    if (h > 0) return h;
    const css = getComputedStyle(document.documentElement).getPropertyValue(
      '--theme-sticky-header-height'
    );
    h = css ? parseInt(css, 10) || 0 : 0;
    if (h > 0) return h;
    const header = document.querySelector('.section-header');
    return header ? header.offsetHeight : 0;
  }

  function offsetTopFromDoc(el) {
    if (window.theme && typeof theme.getOffsetTopFromDoc === 'function') {
      return theme.getOffsetTopFromDoc(el);
    }
    return el.getBoundingClientRect().top + window.scrollY;
  }

  function getTopFilterScrollTarget() {
    const wrapper = document.querySelector('.collection-list-wrapper');
    if (!wrapper) {
      return (
        document.querySelector('.collection-slider-row.for-collections') ||
        document.querySelector('[id*="collection_list_modified"]')
      );
    }
    return (
      wrapper.closest('.collection-slider-row') ||
      wrapper.closest('[id*="collection_list_modified"]') ||
      wrapper.closest('.shopify-section') ||
      wrapper
    );
  }

  function scrollYForTopFilterTarget(el) {
    return Math.max(0, Math.round(offsetTopFromDoc(el) - stickyHeaderHeight()));
  }

  function scrollToTopFilterSection() {
    const target = getTopFilterScrollTarget();
    if (!target) return;
    window.scrollTo({
      top: scrollYForTopFilterTarget(target),
      behavior: 'auto',
    });
  }

  function openFilters() {
    const filterContainer = document.querySelector('filter-container, .filter-container');
    if (filterContainer) {
      filterContainer.classList.add(
        'filter-container--show-filters-desktop',
        'filter-container--show-filters-mobile'
      );
    }
    document.querySelectorAll('[data-toggle-filters]').forEach(function (el) {
      el.classList.add('toggle-btn--revealed-desktop');
    });
  }

  document.addEventListener(
    'click',
    function (evt) {
      const link = evt.target.closest('.collection-list-wrapper a.product-link');
      if (!link) return;
      if (!hrefHasFilterParam(link.getAttribute('href') || link.href)) return;

      const target = getTopFilterScrollTarget();
      const y = target ? scrollYForTopFilterTarget(target) : 0;

      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
        sessionStorage.setItem(SCROLL_KEY, String(y));
      } catch (e) {
        /* private mode / blocked storage */
      }
    },
    true
  );

  function openFiltersFromTopFilter() {
    let flagged = false;
    try {
      flagged = sessionStorage.getItem(STORAGE_KEY) === '1';
      if (flagged) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(SCROLL_KEY);
      }
    } catch (e) {
      return;
    }

    const fromNav = window.__aesthetTopFilterNav === true;
    if (!fromNav && !flagged) return;
    if (!currentUrlHasFilterParam()) return;

    window.__aesthetTopFilterNav = false;
    openFilters();
    scrollToTopFilterSection();
    requestAnimationFrame(function () {
      scrollToTopFilterSection();
      setTimeout(scrollToTopFilterSection, 100);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFiltersFromTopFilter);
  } else {
    openFiltersFromTopFilter();
  }
})();

/**
 * Collection/search → PDP → Back: restore the PLP viewport when native
 * history restoration fails (images, filters, late layout). Yields to
 * footer-newsletter and top-filter scroll takeovers.
 */
(function () {
  const STORAGE_KEY = 'aesthet:plpBackScroll';
  const NEWSLETTER_KEY = 'aesthet_footer_newsletter_scroll';
  const TOP_FILTER_KEY = 'aesthet:openFiltersFromTopFilter';
  const TOP_DELTA = 80;
  const FAR_DELTA = 240;

  function isPlpPage() {
    const body = document.body;
    if (!body) return false;
    return (
      body.classList.contains('template-collection') ||
      body.classList.contains('template-search')
    );
  }

  function currentPageKey() {
    return window.location.pathname + window.location.search;
  }

  function isProductHref(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return /\/products\/[^/]+/.test(url.pathname);
    } catch (e) {
      return /\/products\/[^/]+/.test(href);
    }
  }

  function hrefHasFilterParam(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      for (const key of url.searchParams.keys()) {
        if (key.indexOf('filter.') === 0) return true;
      }
    } catch (e) {
      return href.indexOf('filter.') !== -1;
    }
    return false;
  }

  function hasTakeoverFlags() {
    try {
      if (sessionStorage.getItem(NEWSLETTER_KEY) === '1') return true;
      if (sessionStorage.getItem(TOP_FILTER_KEY) === '1' && /[?&]filter\./.test(window.location.search)) {
        return true;
      }
    } catch (e) {
      /* private mode */
    }
    return window.__aesthetTopFilterNav === true;
  }

  function isBackForwardNav() {
    try {
      const entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (entries && entries[0] && entries[0].type === 'back_forward') return true;
    } catch (e) {
      /* ignore */
    }
    try {
      return performance.navigation && performance.navigation.type === 2;
    } catch (e) {
      return false;
    }
  }

  function readSaved() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') return null;
      if (typeof saved.url !== 'string' || saved.url !== currentPageKey()) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }

  function writeSaved(next) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* private mode / blocked storage */
    }
  }

  function stickyHeaderHeight() {
    let h = 0;
    if (window.theme && typeof theme.stickyHeaderHeight === 'function') {
      h = theme.stickyHeaderHeight() || 0;
    }
    if (h > 0) return h;
    const css = getComputedStyle(document.documentElement).getPropertyValue(
      '--theme-sticky-header-height'
    );
    h = css ? parseInt(css, 10) || 0 : 0;
    if (h > 0) return h;
    const header = document.querySelector('.section-header');
    return header ? header.offsetHeight : 0;
  }

  function nativeLooksWrong(savedY) {
    const y = window.scrollY || window.pageYOffset || 0;
    if (savedY > TOP_DELTA && y < 40) return true;
    return Math.abs(y - savedY) > FAR_DELTA;
  }

  function scrollToSaved(saved) {
    if (!saved) return false;

    const handle = typeof saved.handle === 'string' ? saved.handle : '';
    if (handle) {
      const selector = window.CSS && typeof CSS.escape === 'function'
        ? 'product-block[data-product-handle="' + CSS.escape(handle) + '"]'
        : 'product-block[data-product-handle="' + handle.replace(/"/g, '') + '"]';
      const tile = document.querySelector(selector);
      if (tile) {
        const top = tile.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight();
        window.scrollTo(0, Math.max(0, Math.round(top)));
        return true;
      }
    }

    if (typeof saved.y === 'number' && saved.y > 0) {
      window.scrollTo(0, saved.y);
      return true;
    }

    return false;
  }

  function restoreIfNeeded() {
    if (!isPlpPage() || hasTakeoverFlags() || !isBackForwardNav()) return;

    const saved = readSaved();
    if (!saved || !(saved.y > 0 || saved.handle)) return;
    if (!nativeLooksWrong(saved.y || 0)) return;

    scrollToSaved(saved);
    requestAnimationFrame(function () {
      if (nativeLooksWrong(saved.y || 0)) scrollToSaved(saved);
    });
    setTimeout(function () {
      if (nativeLooksWrong(saved.y || 0)) scrollToSaved(saved);
    }, 150);
  }

  document.addEventListener(
    'click',
    function (evt) {
      if (!isPlpPage()) return;
      const link = evt.target.closest('a.product-link');
      if (!link) return;

      const href = link.getAttribute('href') || link.href;
      if (link.closest('.collection-list-wrapper') && hrefHasFilterParam(href)) return;
      if (!isProductHref(href)) return;

      const block = link.closest('product-block');
      writeSaved({
        url: currentPageKey(),
        y: Math.round(window.scrollY || window.pageYOffset || 0),
        handle: (block && block.getAttribute('data-product-handle')) || '',
      });
    },
    true
  );

  window.addEventListener('pagehide', function () {
    if (!isPlpPage() || hasTakeoverFlags()) return;
    const saved = readSaved();
    writeSaved({
      url: currentPageKey(),
      y: Math.round(window.scrollY || window.pageYOffset || 0),
      handle: saved && saved.url === currentPageKey() ? saved.handle : '',
    });
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) return;
    restoreIfNeeded();
  });

  window.addEventListener('load', restoreIfNeeded);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreIfNeeded);
  } else {
    restoreIfNeeded();
  }
})();

/**
 * Instafeed app: dark CTA overlay on the first tile.
 * Copy from general.social.instafeed_cta_* in the active locale file.
 */
(function () {
  const OVERLAY_CLASS = 'aesthet-instafeed-cta';
  const CHEVRON_SVG =
    '<span class="small-feature-link__chevron" aria-hidden="true">' +
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-right" aria-hidden="true" focusable="false" role="presentation"><path d="m9 18 6-6-6-6"></path></svg>' +
    '</span>';

  function trimText(value) {
    return String(value || '').trim();
  }

  function getCtaCopy() {
    const strings = (window.theme && theme.strings) || {};
    return {
      line1: trimText(strings.instafeedCtaHeadingLine1),
      line2: trimText(strings.instafeedCtaHeadingLine2),
      link: trimText(strings.instafeedCtaLink),
      url: trimText(
        (window.theme && theme.settings && theme.settings.socialInstagramUrl) ||
          'https://www.instagram.com/aesthet.greece/'
      ),
    };
  }

  function ensureOverlay(firstItem) {
    if (!firstItem || firstItem.querySelector('.' + OVERLAY_CLASS)) return;

    const copy = getCtaCopy();
    if (!copy.url) return;
    if (!copy.line1 && !copy.line2 && !copy.link) return;

    const overlay = document.createElement('a');
    overlay.className =
      OVERLAY_CLASS + ' image-overlay image-overlay--bg-shadow image-overlay--edge-boxes';
    overlay.href = copy.url;
    overlay.target = '_blank';
    overlay.rel = 'noopener noreferrer';
    overlay.setAttribute('aria-label', copy.link || copy.line1 || copy.line2 || 'Instagram');

    overlay.innerHTML =
      '<div class="text-overlay text-overlay--tight text-overlay--v-center text-overlay--h-center image-overlay__over">' +
      '<div class="text-overlay__inner text-overlay__inner--pad-standard">' +
      '<div class="text-overlay__text">' +
      '<h2 class="text-overlay__title h4" hidden></h2>' +
      '<div class="text-overlay__button-row text-overlay__button-row--chevron" hidden>' +
      '<span class="text-overlay__button small-feature-link small-feature-link--chevron"></span>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>';

    const title = overlay.querySelector('.text-overlay__title');
    if (copy.line1 || copy.line2) {
      title.hidden = false;
      if (copy.line1) title.appendChild(document.createTextNode(copy.line1));
      if (copy.line1 && copy.line2) title.appendChild(document.createElement('br'));
      if (copy.line2) {
        title.appendChild(document.createTextNode((copy.line1 ? ' ' : '') + copy.line2));
      }
    }

    const buttonRow = overlay.querySelector('.text-overlay__button-row');
    if (copy.link) {
      buttonRow.hidden = false;
      const btn = overlay.querySelector('.small-feature-link');
      btn.textContent = copy.link;
      btn.insertAdjacentHTML('beforeend', CHEVRON_SVG);
    }

    overlay.addEventListener(
      'click',
      function (evt) {
        evt.stopPropagation();
      },
      true
    );

    firstItem.appendChild(overlay);
  }

  function applyToFeed(root) {
    const feed = root && root.id === 'insta-feed' ? root : (root || document).querySelector('#insta-feed');
    if (!feed) return;
    const firstItem = feed.querySelector('.instafeed-new-layout-item');
    ensureOverlay(firstItem);
  }

  function start() {
    applyToFeed(document);

    const feed = document.querySelector('#insta-feed');
    if (!feed || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(function () {
      applyToFeed(feed);
    });
    observer.observe(feed, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
